// src/components/map/MapView.tsx
import React, { useRef, useState, useEffect, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import {
  Box,
  Paper,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  InputAdornment,
  Portal,
  TextField,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  MyLocation as MyLocationIcon,
  EditOutlined as EditOutlinedIcon,
  Close as CloseIcon,
  ScheduleOutlined as ScheduleOutlinedIcon,
  ArrowBackRounded as ArrowBackRoundedIcon,
  SearchRounded as SearchRoundedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
  RefreshRounded as RefreshRoundedIcon,
} from '@mui/icons-material';
import { TaskInfo, TouchPoint, Trip, TripHoverPreview, ViewportOcclusionRect } from '../../models/types';
import {
  formatDistance,
  formatDurationFromMinutes,
  getTripCapacityDelta,
  getTripWorkingTimeDelta,
  getTripWindowMetrics,
  formatWeight,
  formatVolume,
  formatMinutesOfDay,
} from '../../utils/formatters';
import { MAPBOX_ACCESS_TOKEN, MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM, COLORS } from '../../config';
import { useTheme } from '../../context/ThemeContext';
import { Settings as SettingsIcon } from '@mui/icons-material';
import Stack from '@mui/material/Stack';
import { TripEditRequest } from '../../api/routingJobsApi';
import DenseEntityCardBody from '../cards/DenseEntityCardBody';
import {
  buildTripCardModel,
  getTripAccentColor,
} from '../../presentation/cards/models';
import { renderDenseIcon } from '../../presentation/cards/iconRegistry';
import type { DenseIconKey, DenseTone } from '../../presentation/cards/types';
import { canBuildTimelineStripData } from '../timeline/timelineUtils';
import TouchPointPreviewOverlay from './TouchPointPreviewOverlay';
import { RouteGeometryByTrip } from '../../routeGeometry/routeGeometry';
import type { ExecutionMapOverlayModel, ExecutionMapPointState } from './executionMapTypes';

// Set mapbox token
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const LIGHT_MAP_STYLE = 'mapbox://styles/mapbox/streets-v11';
const DARK_MAP_STYLE = 'mapbox://styles/mapbox/dark-v11';
const DEFAULT_TOUCHPOINT_MARKER_SIZE = 14;
const EDIT_HIGHLIGHT_COLOR = '#2563eb';
const EDIT_POINT_SOURCE_ID = 'trip-edit-touchpoints-source';
const EDIT_POINT_HIT_LAYER_ID = 'trip-edit-touchpoints-hit';
const EDIT_POINT_CIRCLE_LAYER_ID = 'trip-edit-touchpoints-circle';
const EDIT_POINT_SELECTED_RING_LAYER_ID = 'trip-edit-touchpoints-selected-ring';
const EDIT_POINT_LABEL_LAYER_ID = 'trip-edit-touchpoints-label';
const EDIT_POINT_SELECTED_BADGE_LAYER_ID = 'trip-edit-touchpoints-selected-badge';
const EDIT_POINT_SELECTED_TICK_LAYER_ID = 'trip-edit-touchpoints-selected-tick';
const EDIT_POLYGON_SOURCE_ID = 'trip-edit-polygon-source';
const EDIT_POLYGON_FILL_LAYER_ID = 'trip-edit-polygon-fill';
const EDIT_POLYGON_LINE_LAYER_ID = 'trip-edit-polygon-line';
const MAP_OVERLAY_GAP_PX = 12;
const EDIT_CARD_FALLBACK_RIGHT_PX = 18;
const EDIT_CARD_FALLBACK_BOTTOM_PX = 20;
const EDIT_CARD_ANCHOR_OFFSET_PX = 18;
const EDIT_CARD_DRAG_THRESHOLD_PX = 6;
const FOCUS_VISIBLE_MARGIN_PX = 28;
const MIN_FOCUS_VISIBLE_WIDTH_PX = 140;
const MIN_FOCUS_VISIBLE_HEIGHT_PX = 110;
const MIN_CAMERA_VIEWPORT_PX = 24;
const FOCUS_TARGET_MARKER_SIZE_PX = 46;
const FOCUS_TARGET_COLOR = '#dc2626';
const TOP_TOOLBAR_TOP_PX = 10;
const TOP_TOOLBAR_RIGHT_PX = 10;
const TOP_TOOLBAR_GAP_PX = 8;
const TOP_TOOLBAR_BUTTON_SIZE_PX = 40;
const FLOATING_PANEL_TOP_PX = 60;
const INSPECT_CAPSULE_RADIUS_PX = 30;
const INSPECT_RECTANGLE_CLICK_SIZE_PX = 44;
const INSPECT_OUTLINE_COLOR = '#2563eb';
const INSPECT_FILL_COLOR = 'rgba(37, 99, 235, 0.14)';
const INSPECT_CENTER_COLOR = '#1d4ed8';
const INSPECT_POLYGON_SOURCE_ID = 'inspect-selection-polygon-source';
const INSPECT_POLYGON_FILL_LAYER_ID = 'inspect-selection-polygon-fill';
const INSPECT_POLYGON_LINE_LAYER_ID = 'inspect-selection-polygon-line';
const EDIT_OUTLINE_DASH_SEQUENCE = [
  [0, 4, 3],
  [0.5, 4, 2.5],
  [1, 4, 2],
  [1.5, 4, 1.5],
  [2, 4, 1],
  [2.5, 4, 0.5],
  [3, 4, 0],
  [0, 0.5, 3, 3.5],
  [0, 1, 3, 3],
  [0, 1.5, 3, 2.5],
  [0, 2, 3, 2],
  [0, 2.5, 3, 1.5],
  [0, 3, 3, 1],
  [0, 3.5, 3, 0.5],
];

interface MapViewProps {
  taskInfo: TaskInfo | null;
  visibleTrips: string[];
  showPolygons: boolean;
  showSequence: boolean;
  allowRouteLines?: boolean;
  allowSequenceLines?: boolean;
  defaultShowRoutes?: boolean;
  routeGeometryByTrip?: RouteGeometryByTrip;
  routeGeometryRefreshAvailable?: boolean;
  routeGeometryRefreshing?: boolean;
  onRefreshRouteGeometry?: () => void;
  showMapLegend?: boolean;
  showMapOptions?: boolean;
  showTripVehicleMarkers?: boolean;
  editEnabled?: boolean;
  editMode: boolean;
  editSourceTrip?: string | null;
  editSubmitting?: boolean;
  selectedTouchPoints: TouchPoint[];
  onMapOptionsChange: (option: 'polygons' | 'sequence') => void;
  onStartTripEdit?: (tripName: string, initialTouchPoints?: TouchPoint[]) => void;
  onTouchPointSelect: (touchPoint: TouchPoint) => void;
  onCancelEdit: () => void;
  onSubmitEdit: (request: TripEditRequest) => Promise<boolean> | void;
  fitPadding?: number | { top: number; bottom: number; left: number; right: number };
  // Optional: center map to a specific point
  focusLngLat?: [number, number] | null;
  focusTouchPointRequest?: { lngLat: [number, number]; requestId: number } | null;
  // Optional: fit the viewport to a specific trip
  focusTripRequest?: { tripName: string; requestId: number } | null;
  focusOcclusionRects?: ViewportOcclusionRect[];
  // Optional: restrict panning/zooming to a bounding box
  maxBounds?: [[number, number], [number, number]] | null;
  // Optional equipment markers to render vehicle locations
  equipmentMarkers?: { id: string; lngLat: [number, number]; type?: string }[];
  // Optional CSV lookups for enriching popups
  lookups?: {
    origin?: { originId: string; address: string; zip?: string; lat: number; lng: number; hourStart?: string; hourEnd?: string };
    clients?: Record<string, { clientId: string; address: string; lat: number; lng: number; hourStart?: string; hourEnd?: string }>;
    drops?: Record<string, { dropId: string; address: string; lat: number; lng: number; hourStart?: string; hourEnd?: string }>;
  };
  // External hover coming from table/cards
  externalHover?: { type: 'origin' | 'client' | 'drop'; id: string } | null;
  externalTouchPoint?: TouchPoint | null;
  externalTouchPointAnchorClient?: { x: number; y: number } | null;
  externalTripHover?: TripHoverPreview | null;
  activeTouchPointKey?: string | null;
  activeTripName?: string | null;
  onClearExternalTouchPoint?: () => void;
  geoCorrectionTouchPoint?: TouchPoint | null;
  geoCorrectionDraftLngLat?: [number, number] | null;
  geoCorrectionSubmitting?: boolean;
  onStartGeoCorrection?: (touchPoint: TouchPoint) => void;
  onGeoCorrectionDraft?: (lngLat: [number, number]) => void;
  onCancelGeoCorrection?: () => void;
  onConfirmGeoCorrection?: () => void;
  onOpenTripTimeline?: (tripName: string, anchor?: { x: number; y: number }) => void;
  executionOverlay?: ExecutionMapOverlayModel;
  executionHoveredActivityId?: string | null;
  onExecutionActivityClick?: (tripId: string, activityId: string) => void;
  onExecutionActivityHover?: (tripId: string | null, activityId: string | null) => void;
  onExecutionTripClick?: (tripId: string) => void;
}

type InspectTool = 'capsule' | 'rectangle';

type FocusPadding = { top: number; bottom: number; left: number; right: number };

interface FocusTargetMarkerState {
  kind: 'touch-point' | 'trip-vehicle';
  lngLat: [number, number];
  requestId: number;
}

interface LocalRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

interface InspectCapsule {
  kind: 'capsule';
  start: { x: number; y: number };
  end: { x: number; y: number };
  radius: number;
  polygonLngLats?: [number, number][];
}

interface InspectRectangle {
  kind: 'rectangle';
  start: { x: number; y: number };
  end: { x: number; y: number };
  polygonLngLats?: [number, number][];
}

type InspectGeometry = InspectCapsule | InspectRectangle;

interface SelectionTripSummary {
  tripName: string;
  touchPoints: TouchPoint[];
  pointCount: number;
  weightKg: number;
  volumeCuFt: number;
  estimatedDistanceKm: number;
  estimatedTimeMinutes: number;
}

interface SelectionSummary {
  touchPoints: TouchPoint[];
  totalWeightKg: number;
  totalVolumeCuFt: number;
  totalEstimatedDistanceKm: number;
  totalEstimatedTimeMinutes: number;
  tripSummaries: SelectionTripSummary[];
}

interface InspectSelection extends SelectionSummary {
  geometry: InspectGeometry;
}

interface MoveScopeSummary {
  key: string;
  label: string;
  sourceTripNames: string[];
  touchPoints: TouchPoint[];
  pointCount: number;
  weightKg: number;
  volumeCuFt: number;
  estimatedDistanceKm: number;
  estimatedTimeMinutes: number;
}

interface DestinationCapacitySnapshot {
  weightKg: number | null;
  touchPoints: number | null;
  volumeCuFt: number | null;
  distanceKm: number | null;
  timeMinutes: number | null;
}

interface DestinationOption {
  id: string;
  label: string;
  subtitle: string;
  kind: 'existing-trip' | 'new-trip-vehicle';
  trip?: Trip;
  vehicleId?: string;
  capacities: DestinationCapacitySnapshot;
  disabled: boolean;
}

type MoveCardPhase = 'summary' | 'destinations' | 'new-trip';

type DestinationCapacityKey = keyof DestinationCapacitySnapshot;

type DestinationWarningSet = Set<DestinationCapacityKey>;

interface VehicleCapacitySeed {
  vehicleId: string;
  driverId: string;
  label: string;
  sourceTripName: string;
  capacities: DestinationCapacitySnapshot;
}

const createFocusTargetMarkerElement = (kind: FocusTargetMarkerState['kind']): HTMLDivElement => {
  const element = document.createElement('div');
  element.className = `map-focus-target map-focus-target--${kind}`;
  element.style.width = `${FOCUS_TARGET_MARKER_SIZE_PX}px`;
  element.style.height = `${FOCUS_TARGET_MARKER_SIZE_PX}px`;
  element.style.borderRadius = '50%';
  element.style.border = `2px solid ${FOCUS_TARGET_COLOR}`;
  element.style.boxSizing = 'border-box';
  element.style.position = 'relative';
  element.style.pointerEvents = 'none';
  element.style.filter = 'drop-shadow(0 2px 5px rgba(127, 29, 29, 0.36))';

  const tickLength = 12;
  const tickThickness = 2;
  const tickOffset = -1;
  const tickStyles: Array<Partial<CSSStyleDeclaration>> = [
    {
      left: `${tickOffset}px`,
      top: '50%',
      width: `${tickLength}px`,
      height: `${tickThickness}px`,
      transform: 'translateY(-50%)',
    },
    {
      right: `${tickOffset}px`,
      top: '50%',
      width: `${tickLength}px`,
      height: `${tickThickness}px`,
      transform: 'translateY(-50%)',
    },
    {
      left: '50%',
      top: `${tickOffset}px`,
      width: `${tickThickness}px`,
      height: `${tickLength}px`,
      transform: 'translateX(-50%)',
    },
    {
      left: '50%',
      bottom: `${tickOffset}px`,
      width: `${tickThickness}px`,
      height: `${tickLength}px`,
      transform: 'translateX(-50%)',
    },
  ];

  tickStyles.forEach((style) => {
    const tick = document.createElement('span');
    tick.style.position = 'absolute';
    tick.style.background = FOCUS_TARGET_COLOR;
    tick.style.borderRadius = '999px';
    Object.assign(tick.style, style);
    element.appendChild(tick);
  });

  return element;
};

const MapView: React.FC<MapViewProps> = ({
  taskInfo,
  visibleTrips,
  showPolygons,
  showSequence,
  allowRouteLines = true,
  allowSequenceLines = true,
  defaultShowRoutes = true,
  routeGeometryByTrip,
  routeGeometryRefreshAvailable = false,
  routeGeometryRefreshing = false,
  onRefreshRouteGeometry,
  showMapLegend = true,
  showMapOptions = true,
  showTripVehicleMarkers = true,
  editEnabled = true,
  editMode,
  editSourceTrip,
  editSubmitting = false,
  selectedTouchPoints,
  onMapOptionsChange,
  onStartTripEdit,
  onTouchPointSelect,
  onCancelEdit,
  onSubmitEdit,
  fitPadding,
  focusLngLat,
  focusTouchPointRequest,
  focusTripRequest,
  focusOcclusionRects = [],
  maxBounds,
  equipmentMarkers,
  lookups,
  externalHover,
  externalTouchPoint,
  externalTouchPointAnchorClient,
  externalTripHover,
  activeTouchPointKey,
  activeTripName,
  onClearExternalTouchPoint,
  geoCorrectionTouchPoint,
  geoCorrectionDraftLngLat,
  geoCorrectionSubmitting = false,
  onStartGeoCorrection,
  onGeoCorrectionDraft,
  onCancelGeoCorrection,
  onConfirmGeoCorrection,
  onOpenTripTimeline,
  executionOverlay,
  executionHoveredActivityId,
  onExecutionActivityClick,
  onExecutionActivityHover,
  onExecutionTripClick,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const executionMarkerElementsRef = useRef<Record<string, HTMLDivElement>>({});
  const executionRenderedTripIdsRef = useRef<string[]>([]);
  const tripTouchPointMarkersRef = useRef<Record<string, mapboxgl.Marker[]>>({});
  const focusTargetMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const previousEditSourceTripRef = useRef<string | null>(null);
  const syncEditOverlayRef = useRef<() => void>(() => {});
  const syncInspectOverlayRef = useRef<() => void>(() => {});
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hoveredTripInfo, setHoveredTripInfo] = useState<Trip | null>(null);
  const [hoveredTouchPoint, setHoveredTouchPoint] = useState<TouchPoint | null>(null);
  const [infoPosition, setInfoPosition] = useState<{ x: number; y: number } | null>(null);
  const [tpInfoPosition, setTpInfoPosition] = useState<{ x: number; y: number } | null>(null);
  const [externalTpInfo, setExternalTpInfo] = useState<{
    tp: TouchPoint;
    position: { x: number; y: number };
    positionMode: 'map' | 'viewport';
  } | null>(null);
  const [externalTripInfo, setExternalTripInfo] = useState<{
    trip: Trip;
    position: { x: number; y: number };
  } | null>(null);
  const [focusTargetMarker, setFocusTargetMarker] = useState<FocusTargetMarkerState | null>(null);
  const tripCardHoveredRef = useRef(false);
  const tripCardHideTimerRef = useRef<number | null>(null);
  const tpCardHoveredRef = useRef(false);
  const tpCardHideTimerRef = useRef<number | null>(null);
  const lastAutoFitSignatureRef = useRef<string | null>(null);
  const tripInfoCardRef = useRef<HTMLDivElement | null>(null);
  const editCardRef = useRef<HTMLDivElement | null>(null);
  const editCardResizeObserverRef = useRef<ResizeObserver | null>(null);
  const editCardDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    dragged: boolean;
  } | null>(null);
  const [tripInfoCardSize, setTripInfoCardSize] = useState({ width: 350, height: 240 });
  const [editCardSize, setEditCardSize] = useState({ width: 360, height: 330 });
  const [editCardPosition, setEditCardPosition] = useState<{ x: number; y: number } | null>(null);
  const [editCardPositionMode, setEditCardPositionMode] = useState<'anchored' | 'manual'>('anchored');
  const [editCardDragging, setEditCardDragging] = useState(false);
  const editCardSizeRef = useRef(editCardSize);
  const { mode } = useTheme();

  useEffect(() => {
    Object.entries(executionMarkerElementsRef.current).forEach(([activityId, element]) => {
      const highlighted = activityId === executionHoveredActivityId;
      element.style.boxShadow = highlighted
        ? '0 0 0 5px rgba(37,99,235,0.28), 0 3px 10px rgba(15,23,42,0.38)'
        : element.dataset.defaultBoxShadow || '';
      element.style.zIndex = highlighted ? '5' : '';
    });
  }, [executionHoveredActivityId]);
  const [mapOptionsVisible, setMapOptionsVisible] = useState(false);
  const [inspectMode, setInspectMode] = useState(false);
  const [inspectTool, setInspectTool] = useState<InspectTool>('capsule');
  const [inspectDraftGeometry, setInspectDraftGeometry] = useState<InspectGeometry | null>(null);
  const [inspectSelection, setInspectSelection] = useState<InspectSelection | null>(null);
  const [moveCardPhase, setMoveCardPhase] = useState<MoveCardPhase>('summary');
  const moveCardPhaseRef = useRef<MoveCardPhase>('summary');
  const [activeMoveScope, setActiveMoveScope] = useState<MoveScopeSummary | null>(null);
  const activeMoveScopeRef = useRef<MoveScopeSummary | null>(null);
  const editSubmittingRef = useRef(editSubmitting);
  const submitMoveScopeRef = useRef<(destinationTripId: string) => void>(() => {});
  const [destinationSearchTerm, setDestinationSearchTerm] = useState('');
  const inspectPointerRef = useRef<{
    pointerId: number;
    origin: { x: number; y: number };
  } | null>(null);
  const [geoLegendExpanded, setGeoLegendExpanded] = useState(false);
  const [showRoutes, setShowRoutes] = useState<boolean>(defaultShowRoutes);
  const animatedEditOutlineLayerIdsRef = useRef<string[]>([]);
  const inspectAvailable = Boolean(taskInfo && editEnabled);

  const clearFocusTargetMarker = useCallback(() => {
    setFocusTargetMarker(null);
  }, []);

  const toMinutesOfDay = (value: unknown): number | null => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return null;
    if (n <= 1440) return Math.round(n);
    if (n <= 86400) return Math.round(n / 60);
    const date = new Date(n * 1000);
    if (!Number.isFinite(date.getTime())) return null;
    return (date.getHours() * 60) + date.getMinutes();
  };

  const getTouchPointEditLabel = useCallback((touchPoint: TouchPoint): string => {
    const tenantKey = String(taskInfo?.tenant ?? '').toLowerCase();
    const attemptStart = toMinutesOfDay(touchPoint.attemptTime?.start);
    const attemptEnd = toMinutesOfDay(touchPoint.attemptTime?.end);
    const weightKg =
      Math.max(0, Number(touchPoint.itemSummary?.dropWtKg ?? 0)) +
      Math.max(0, Number(touchPoint.itemSummary?.pickWtKg ?? 0));
    const roundWeight = (value: number): string => String(Math.max(0, Math.round(value)));
    const getAttemptLabel = (minutes: number | null): string | null => {
      if (minutes == null) {
        return null;
      }
      return formatMinutesOfDay(minutes % (24 * 60));
    };

    if (tenantKey.includes('jumbobuy')) {
      const attemptLabel = getAttemptLabel(attemptStart) ?? getAttemptLabel(attemptEnd);
      if (attemptLabel) {
        return attemptLabel;
      }
      return String(touchPoint.seqNo);
    }

    if (tenantKey.includes('gemba') || tenantKey.includes('reworld')) {
      return roundWeight(weightKg);
    }

    const attemptLabel = getAttemptLabel(attemptStart) ?? getAttemptLabel(attemptEnd);
    if (attemptLabel) {
      return attemptLabel;
    }

    return weightKg > 0 ? roundWeight(weightKg) : String(touchPoint.seqNo);
  }, [taskInfo?.tenant]);

  const normalizeLngLat = (candidate: any): [number, number] | null => {
    if (!Array.isArray(candidate) || candidate.length < 2) return null;
    const lng = Number(candidate[0]);
    const lat = Number(candidate[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    return [lng, lat];
  };

  const removeLayerIfExists = useCallback((layerId: string) => {
    if (map.current?.getLayer(layerId)) {
      map.current.removeLayer(layerId);
    }
  }, []);

  const removeSourceIfExists = useCallback((sourceId: string) => {
    if (map.current?.getSource(sourceId)) {
      map.current.removeSource(sourceId);
    }
  }, []);

  const removeBaseTouchPointMarkersForTrip = useCallback((tripName: string | null | undefined) => {
    if (!tripName) {
      return;
    }
    const markers = tripTouchPointMarkersRef.current[tripName] ?? [];
    markers.forEach((marker) => marker.remove());
    tripTouchPointMarkersRef.current[tripName] = [];
  }, []);

  const projectLngLatToViewport = useCallback((lngLat: [number, number] | null): { x: number; y: number } | null => {
    if (!lngLat || !map.current || !mapContainer.current) {
      return null;
    }

    const projected = map.current.project(lngLat);
    const containerRect = mapContainer.current.getBoundingClientRect();
    return {
      x: containerRect.left + projected.x,
      y: containerRect.top + projected.y,
    };
  }, []);

  const projectLngLatToContainer = useCallback((lngLat: [number, number] | null): { x: number; y: number } | null => {
    if (!lngLat || !map.current || !mapContainer.current) {
      return null;
    }

    const projected = map.current.project(lngLat);
    return {
      x: projected.x,
      y: projected.y,
    };
  }, []);

  const projectTouchPointToViewport = useCallback((touchPoint: TouchPoint | null | undefined) => {
    if (!touchPoint) {
      return null;
    }
    const coords = normalizeLngLat(touchPoint.lngLat);
    if (!coords) {
      return null;
    }
    return projectLngLatToViewport(coords);
  }, [projectLngLatToViewport]);

  const rectsIntersect = (left: LocalRect, right: LocalRect): boolean => (
    left.left < right.right &&
    left.right > right.left &&
    left.top < right.bottom &&
    left.bottom > right.top
  );

  const buildLocalRect = (left: number, top: number, right: number, bottom: number): LocalRect => ({
    left,
    top,
    right,
    bottom,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  });

  const getLocalFocusOcclusionRects = (): LocalRect[] => {
    if (!mapContainer.current) {
      return [];
    }

    const mapRect = mapContainer.current.getBoundingClientRect();
    return focusOcclusionRects
      .map((rect) => {
        const left = Math.max(0, Math.min(mapRect.width, rect.left - mapRect.left));
        const top = Math.max(0, Math.min(mapRect.height, rect.top - mapRect.top));
        const right = Math.max(0, Math.min(mapRect.width, rect.right - mapRect.left));
        const bottom = Math.max(0, Math.min(mapRect.height, rect.bottom - mapRect.top));
        return buildLocalRect(left, top, right, bottom);
      })
      .filter((rect) => rect.width > 1 && rect.height > 1);
  };

  const getLargestVisibleFocusRect = (): LocalRect | null => {
    if (!mapContainer.current) {
      return null;
    }

    const mapRect = mapContainer.current.getBoundingClientRect();
    const fullRect = buildLocalRect(0, 0, mapRect.width, mapRect.height);
    const blockers = getLocalFocusOcclusionRects();
    if (!blockers.length) {
      return fullRect;
    }

    const xEdges = new Set<number>([0, fullRect.right]);
    const yEdges = new Set<number>([0, fullRect.bottom]);
    blockers.forEach((blocker) => {
      xEdges.add(blocker.left);
      xEdges.add(blocker.right);
      yEdges.add(blocker.top);
      yEdges.add(blocker.bottom);
    });

    const xs = Array.from(xEdges).sort((left, right) => left - right);
    const ys = Array.from(yEdges).sort((left, right) => left - right);
    const mapCenter = { x: fullRect.width / 2, y: fullRect.height / 2 };
    let bestRect: LocalRect | null = null;
    let bestArea = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (let xIndex = 0; xIndex < xs.length - 1; xIndex += 1) {
      for (let yIndex = 0; yIndex < ys.length - 1; yIndex += 1) {
        const candidate = buildLocalRect(xs[xIndex], ys[yIndex], xs[xIndex + 1], ys[yIndex + 1]);
        if (candidate.width < MIN_FOCUS_VISIBLE_WIDTH_PX || candidate.height < MIN_FOCUS_VISIBLE_HEIGHT_PX) {
          continue;
        }
        if (blockers.some((blocker) => rectsIntersect(candidate, blocker))) {
          continue;
        }

        const area = candidate.width * candidate.height;
        const centerX = candidate.left + (candidate.width / 2);
        const centerY = candidate.top + (candidate.height / 2);
        const distance = Math.hypot(centerX - mapCenter.x, centerY - mapCenter.y);
        if (area > bestArea || (area === bestArea && distance < bestDistance)) {
          bestRect = candidate;
          bestArea = area;
          bestDistance = distance;
        }
      }
    }

    return bestRect ?? fullRect;
  };

  const clampFocusPadding = (padding: FocusPadding, width: number, height: number): FocusPadding => {
    const safeWidth = Number.isFinite(width) ? Math.max(0, width) : 0;
    const safeHeight = Number.isFinite(height) ? Math.max(0, height) : 0;
    const safePadding = {
      top: Number.isFinite(padding.top) ? Math.max(0, padding.top) : 0,
      bottom: Number.isFinite(padding.bottom) ? Math.max(0, padding.bottom) : 0,
      left: Number.isFinite(padding.left) ? Math.max(0, padding.left) : 0,
      right: Number.isFinite(padding.right) ? Math.max(0, padding.right) : 0,
    };
    const maxHorizontalPadding = Math.max(0, safeWidth - MIN_CAMERA_VIEWPORT_PX);
    const maxVerticalPadding = Math.max(0, safeHeight - MIN_CAMERA_VIEWPORT_PX);
    const horizontalPadding = safePadding.left + safePadding.right;
    const verticalPadding = safePadding.top + safePadding.bottom;
    const horizontalScale = horizontalPadding > maxHorizontalPadding && horizontalPadding > 0
      ? maxHorizontalPadding / horizontalPadding
      : 1;
    const verticalScale = verticalPadding > maxVerticalPadding && verticalPadding > 0
      ? maxVerticalPadding / verticalPadding
      : 1;

    return {
      top: Math.round(safePadding.top * verticalScale),
      bottom: Math.round(safePadding.bottom * verticalScale),
      left: Math.round(safePadding.left * horizontalScale),
      right: Math.round(safePadding.right * horizontalScale),
    };
  };

  const getFocusPadding = (): FocusPadding | undefined => {
    if (!mapContainer.current) {
      return undefined;
    }

    const mapRect = mapContainer.current.getBoundingClientRect();
    const visibleRect = getLargestVisibleFocusRect();
    if (!visibleRect) {
      return undefined;
    }

    return clampFocusPadding({
      top: visibleRect.top + FOCUS_VISIBLE_MARGIN_PX,
      bottom: mapRect.height - visibleRect.bottom + FOCUS_VISIBLE_MARGIN_PX,
      left: visibleRect.left + FOCUS_VISIBLE_MARGIN_PX,
      right: mapRect.width - visibleRect.right + FOCUS_VISIBLE_MARGIN_PX,
    }, mapRect.width, mapRect.height);
  };

  const getTouchPointWeightKg = (touchPoint: TouchPoint): number => (
    Math.max(0, Number(touchPoint.itemSummary?.dropWtKg ?? 0)) +
    Math.max(0, Number(touchPoint.itemSummary?.pickWtKg ?? 0))
  );

  const getTouchPointVolumeCuFt = (touchPoint: TouchPoint): number => (
    Math.max(0, Number(touchPoint.itemSummary?.volCuFt ?? 0))
  );

  const getRelativeClientPoint = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    if (!mapContainer.current) {
      return null;
    }

    const rect = mapContainer.current.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const clearInspectOverlay = useCallback(() => {
    removeLayerIfExists(INSPECT_POLYGON_LINE_LAYER_ID);
    removeLayerIfExists(INSPECT_POLYGON_FILL_LAYER_ID);
    removeSourceIfExists(INSPECT_POLYGON_SOURCE_ID);
  }, [removeLayerIfExists, removeSourceIfExists]);

  const clearInspectState = useCallback(() => {
    inspectPointerRef.current = null;
    setInspectDraftGeometry(null);
    setInspectSelection(null);
    clearInspectOverlay();
  }, [clearInspectOverlay]);

  const getTripDurationMinutes = useCallback((trip: Trip): number => {
    const assignmentDuration = Number(trip.assignmentMeta?.tripDurationMinutes);
    if (Number.isFinite(assignmentDuration) && assignmentDuration > 0) {
      return assignmentDuration;
    }

    const workingDelta = getTripWorkingTimeDelta(trip);
    if (workingDelta?.used != null && Number.isFinite(workingDelta.used)) {
      return Math.max(0, workingDelta.used);
    }

    const windowDuration = getTripWindowMetrics(trip).durationMinutes;
    if (windowDuration != null && Number.isFinite(windowDuration)) {
      return Math.max(0, windowDuration);
    }

    return 0;
  }, []);

  const getSelectionEstimateForTrip = useCallback((trip: Trip, selectedPoints: TouchPoint[]) => {
    if (!trip.tpDetails.length || !selectedPoints.length) {
      return {
        estimatedDistanceKm: 0,
        estimatedTimeMinutes: 0,
      };
    }

    const selectedPointRatio = selectedPoints.length / trip.tpDetails.length;
    return {
      estimatedDistanceKm: Math.max(0, trip.distKm * selectedPointRatio),
      estimatedTimeMinutes: Math.max(0, getTripDurationMinutes(trip) * selectedPointRatio),
    };
  }, [getTripDurationMinutes]);

  const buildSelectionSummary = useCallback((
    touchPoints: TouchPoint[],
    fallbackTripNames: string[] = []
  ): SelectionSummary => {
    if (!taskInfo) {
      return {
        touchPoints,
        totalWeightKg: 0,
        totalVolumeCuFt: 0,
        totalEstimatedDistanceKm: 0,
        totalEstimatedTimeMinutes: 0,
        tripSummaries: [],
      };
    }

    const touchPointIdSet = new Set(touchPoints.map((touchPoint) => touchPoint.tpId));
    const tripSummaries: SelectionTripSummary[] = [];

    taskInfo.trips.forEach((trip) => {
      const selectedPoints = trip.tpDetails
        .filter((touchPoint) => touchPointIdSet.has(touchPoint.tpId))
        .sort((left, right) => left.seqNo - right.seqNo);

      if (!selectedPoints.length && !fallbackTripNames.includes(trip.name)) {
        return;
      }

      const weightKg = selectedPoints.reduce((sum, touchPoint) => sum + getTouchPointWeightKg(touchPoint), 0);
      const volumeCuFt = selectedPoints.reduce((sum, touchPoint) => sum + getTouchPointVolumeCuFt(touchPoint), 0);
      const estimates = getSelectionEstimateForTrip(trip, selectedPoints);

      tripSummaries.push({
        tripName: trip.name,
        touchPoints: selectedPoints,
        pointCount: selectedPoints.length,
        weightKg,
        volumeCuFt,
        estimatedDistanceKm: estimates.estimatedDistanceKm,
        estimatedTimeMinutes: estimates.estimatedTimeMinutes,
      });
    });

    tripSummaries.sort((left, right) => {
      if (right.pointCount !== left.pointCount) {
        return right.pointCount - left.pointCount;
      }
      if (right.weightKg !== left.weightKg) {
        return right.weightKg - left.weightKg;
      }
      return left.tripName.localeCompare(right.tripName);
    });

    return {
      touchPoints,
      totalWeightKg: tripSummaries.reduce((sum, summary) => sum + summary.weightKg, 0),
      totalVolumeCuFt: tripSummaries.reduce((sum, summary) => sum + summary.volumeCuFt, 0),
      totalEstimatedDistanceKm: tripSummaries.reduce((sum, summary) => sum + summary.estimatedDistanceKm, 0),
      totalEstimatedTimeMinutes: tripSummaries.reduce((sum, summary) => sum + summary.estimatedTimeMinutes, 0),
      tripSummaries,
    };
  }, [getSelectionEstimateForTrip, taskInfo]);

  const getTripAvailableCapacity = useCallback((trip: Trip): DestinationCapacitySnapshot => {
    const extra = (trip.tripMeta?.extra ?? {}) as Record<string, unknown>;
    const availableCapacity = (extra.availableCapacity ?? {}) as Record<string, unknown>;
    const weightDelta = getTripCapacityDelta(trip);
    const workingDelta = getTripWorkingTimeDelta(trip);
    const volumeCc = Number(availableCapacity.avlVolCc);

    return {
      weightKg: Number.isFinite(Number(availableCapacity.avlKgs))
        ? Number(availableCapacity.avlKgs)
        : (weightDelta?.delta ?? null),
      touchPoints: Number.isFinite(Number(availableCapacity.avlTp))
        ? Number(availableCapacity.avlTp)
        : null,
      volumeCuFt: Number.isFinite(volumeCc)
        ? volumeCc / 28316.8466
        : null,
      distanceKm: Number.isFinite(Number(availableCapacity.avlKms))
        ? Number(availableCapacity.avlKms)
        : null,
      timeMinutes: workingDelta?.delta ?? null,
    };
  }, []);

  const getVehicleCapacitySeed = useCallback((trip: Trip): VehicleCapacitySeed | null => {
    const vehicleId = String(trip.vehDetails?.vehId ?? '').trim();
    const driverId = String((trip.vehDetails as any)?.driverId ?? (trip.tripMeta?.extra as any)?.driverId ?? '').trim();
    if (!vehicleId || !driverId) {
      return null;
    }

    const available = getTripAvailableCapacity(trip);
    const workingDelta = getTripWorkingTimeDelta(trip);
    const fullWeightCapacity = trip.vehDetails.capacityKg
      ?? (available.weightKg != null ? trip.totalWtKg + available.weightKg : null);
    const fullTouchPointCapacity = available.touchPoints != null
      ? trip.tpDetails.length + available.touchPoints
      : null;
    const fullVolumeCapacity = available.volumeCuFt != null
      ? trip.volCuFt + available.volumeCuFt
      : null;
    const fullDistanceCapacity = available.distanceKm != null
      ? trip.distKm + available.distanceKm
      : null;
    const fullTimeCapacity = workingDelta?.limit
      ?? (available.timeMinutes != null ? getTripDurationMinutes(trip) + available.timeMinutes : null);

    return {
      vehicleId,
      driverId,
      label: vehicleId,
      sourceTripName: trip.name,
      capacities: {
        weightKg: fullWeightCapacity,
        touchPoints: fullTouchPointCapacity,
        volumeCuFt: fullVolumeCapacity,
        distanceKm: fullDistanceCapacity,
        timeMinutes: fullTimeCapacity,
      },
    };
  }, [getTripAvailableCapacity, getTripDurationMinutes]);

  const getDestinationWarnings = useCallback((
    capacities: DestinationCapacitySnapshot,
    scope: MoveScopeSummary | null
  ): DestinationWarningSet => {
    const warnings: DestinationWarningSet = new Set();
    if (!scope) {
      return warnings;
    }

    if (capacities.weightKg != null && capacities.weightKg < scope.weightKg) {
      warnings.add('weightKg');
    }
    if (capacities.touchPoints != null && capacities.touchPoints < scope.pointCount) {
      warnings.add('touchPoints');
    }
    if (capacities.volumeCuFt != null && capacities.volumeCuFt < scope.volumeCuFt) {
      warnings.add('volumeCuFt');
    }
    if (
      capacities.distanceKm != null &&
      scope.estimatedDistanceKm > 0 &&
      capacities.distanceKm < scope.estimatedDistanceKm
    ) {
      warnings.add('distanceKm');
    }
    if (
      capacities.timeMinutes != null &&
      scope.estimatedTimeMinutes > 0 &&
      capacities.timeMinutes < scope.estimatedTimeMinutes
    ) {
      warnings.add('timeMinutes');
    }
    return warnings;
  }, []);

  const getInspectRectangleBounds = useCallback((geometry: InspectRectangle) => ({
    left: Math.min(geometry.start.x, geometry.end.x),
    top: Math.min(geometry.start.y, geometry.end.y),
    right: Math.max(geometry.start.x, geometry.end.x),
    bottom: Math.max(geometry.start.y, geometry.end.y),
  }), []);

  const closeInspectPolygon = (lngLats: [number, number][]): [number, number][] => {
    if (!lngLats.length) {
      return lngLats;
    }
    const first = lngLats[0];
    const last = lngLats[lngLats.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      return lngLats;
    }
    return [...lngLats, first];
  };

  const unprojectInspectPoint = useCallback((point: { x: number; y: number }): [number, number] | null => {
    if (!map.current) {
      return null;
    }
    const lngLat = map.current.unproject([point.x, point.y]);
    return [lngLat.lng, lngLat.lat];
  }, []);

  const buildInspectPolygonLngLats = useCallback((geometry: InspectGeometry): [number, number][] | null => {
    if (!map.current) {
      return null;
    }

    let screenPoints: { x: number; y: number }[] = [];
    if (geometry.kind === 'rectangle') {
      const bounds = getInspectRectangleBounds(geometry);
      screenPoints = [
        { x: bounds.left, y: bounds.top },
        { x: bounds.right, y: bounds.top },
        { x: bounds.right, y: bounds.bottom },
        { x: bounds.left, y: bounds.bottom },
      ];
    } else {
      const deltaX = geometry.end.x - geometry.start.x;
      const deltaY = geometry.end.y - geometry.start.y;
      const segmentLength = Math.hypot(deltaX, deltaY);
      const sampleCount = 18;

      if (segmentLength <= 1) {
        screenPoints = Array.from({ length: sampleCount * 2 }, (_, index) => {
          const angle = (index / (sampleCount * 2)) * Math.PI * 2;
          return {
            x: geometry.start.x + Math.cos(angle) * geometry.radius,
            y: geometry.start.y + Math.sin(angle) * geometry.radius,
          };
        });
      } else {
        const angle = Math.atan2(deltaY, deltaX);
        const endArc = Array.from({ length: sampleCount + 1 }, (_, index) => {
          const theta = angle - (Math.PI / 2) + ((index / sampleCount) * Math.PI);
          return {
            x: geometry.end.x + Math.cos(theta) * geometry.radius,
            y: geometry.end.y + Math.sin(theta) * geometry.radius,
          };
        });
        const startArc = Array.from({ length: sampleCount + 1 }, (_, index) => {
          const theta = angle + (Math.PI / 2) + ((index / sampleCount) * Math.PI);
          return {
            x: geometry.start.x + Math.cos(theta) * geometry.radius,
            y: geometry.start.y + Math.sin(theta) * geometry.radius,
          };
        });
        screenPoints = [...endArc, ...startArc];
      }
    }

    const lngLats = screenPoints
      .map(unprojectInspectPoint)
      .filter((lngLat): lngLat is [number, number] => Boolean(lngLat));
    return lngLats.length >= 3 ? closeInspectPolygon(lngLats) : null;
  }, [getInspectRectangleBounds, unprojectInspectPoint]);

  const buildInspectGeometry = useCallback(
    (
      tool: InspectTool,
      start: { x: number; y: number },
      end: { x: number; y: number },
      finalize = false
    ): InspectGeometry => {
      let geometry: InspectGeometry;
      if (tool === 'capsule') {
        geometry = {
          kind: 'capsule',
          start,
          end,
          radius: INSPECT_CAPSULE_RADIUS_PX,
        };
      } else if (
        finalize &&
        Math.abs(end.x - start.x) < 2 &&
        Math.abs(end.y - start.y) < 2
      ) {
        geometry = {
          kind: 'rectangle',
          start,
          end: {
            x: start.x + INSPECT_RECTANGLE_CLICK_SIZE_PX,
            y: start.y + INSPECT_RECTANGLE_CLICK_SIZE_PX,
          },
        };
      } else {
        geometry = {
          kind: 'rectangle',
          start,
          end,
        };
      }

      if (!finalize) {
        return geometry;
      }

      const polygonLngLats = buildInspectPolygonLngLats(geometry);
      return polygonLngLats ? { ...geometry, polygonLngLats } : geometry;
    },
    [buildInspectPolygonLngLats]
  );

  const isPointInsideInspectGeometry = useCallback(
    (point: { x: number; y: number }, geometry: InspectGeometry): boolean => {
      if (geometry.kind === 'rectangle') {
        const bounds = getInspectRectangleBounds(geometry);
        return (
          point.x >= bounds.left &&
          point.x <= bounds.right &&
          point.y >= bounds.top &&
          point.y <= bounds.bottom
        );
      }

      const deltaX = geometry.end.x - geometry.start.x;
      const deltaY = geometry.end.y - geometry.start.y;
      const segmentLengthSquared = (deltaX * deltaX) + (deltaY * deltaY);

      if (segmentLengthSquared <= 1) {
        return Math.hypot(point.x - geometry.start.x, point.y - geometry.start.y) <= geometry.radius;
      }

      const t = Math.max(
        0,
        Math.min(
          1,
          (((point.x - geometry.start.x) * deltaX) + ((point.y - geometry.start.y) * deltaY)) / segmentLengthSquared
        )
      );
      const nearestX = geometry.start.x + (deltaX * t);
      const nearestY = geometry.start.y + (deltaY * t);
      return Math.hypot(point.x - nearestX, point.y - nearestY) <= geometry.radius;
    },
    [getInspectRectangleBounds]
  );

  const isLngLatOnInspectPolygonBoundary = useCallback((
    point: [number, number],
    start: [number, number],
    end: [number, number]
  ): boolean => {
    const lengthSquared = ((end[0] - start[0]) ** 2) + ((end[1] - start[1]) ** 2);
    if (lengthSquared <= Number.EPSILON) {
      return false;
    }

    const cross = ((point[1] - start[1]) * (end[0] - start[0])) - ((point[0] - start[0]) * (end[1] - start[1]));
    if (Math.abs(cross) > 1e-10) {
      return false;
    }
    const dot = ((point[0] - start[0]) * (end[0] - start[0])) + ((point[1] - start[1]) * (end[1] - start[1]));
    if (dot < 0) {
      return false;
    }
    return dot <= lengthSquared;
  }, []);

  const isLngLatInsideInspectPolygon = useCallback((point: [number, number], polygon: [number, number][]): boolean => {
    if (polygon.length < 4) {
      return false;
    }

    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    const ring = first[0] === last[0] && first[1] === last[1]
      ? polygon.slice(0, -1)
      : polygon;
    if (ring.length < 3) {
      return false;
    }

    let inside = false;
    for (let index = 0, previousIndex = ring.length - 1; index < ring.length; previousIndex = index, index += 1) {
      const current = ring[index];
      const previous = ring[previousIndex];

      if (isLngLatOnInspectPolygonBoundary(point, previous, current)) {
        return true;
      }

      const intersects = (
        (current[1] > point[1]) !== (previous[1] > point[1]) &&
        point[0] < (((previous[0] - current[0]) * (point[1] - current[1])) / (previous[1] - current[1])) + current[0]
      );
      if (intersects) {
        inside = !inside;
      }
    }

    return inside;
  }, [isLngLatOnInspectPolygonBoundary]);

  const buildInspectSelection = useCallback((geometry: InspectGeometry): InspectSelection | null => {
    if (!map.current || !taskInfo) {
      return null;
    }

    const touchPoints: TouchPoint[] = [];

    taskInfo.trips.forEach((trip) => {
      if (!visibleTrips.includes(trip.name)) {
        return;
      }

      const selectedPoints = trip.tpDetails
        .filter((touchPoint) => {
          const lngLat = normalizeLngLat(touchPoint.lngLat);
          if (!lngLat || !map.current) {
            return false;
          }

          if (geometry.polygonLngLats) {
            return isLngLatInsideInspectPolygon(lngLat, geometry.polygonLngLats);
          }

          const projected = map.current.project(lngLat);
          return isPointInsideInspectGeometry({ x: projected.x, y: projected.y }, geometry);
        })
        .sort((left, right) => left.seqNo - right.seqNo);

      if (!selectedPoints.length) {
        return;
      }
      touchPoints.push(...selectedPoints);
    });

    const selectionSummary = buildSelectionSummary(touchPoints);

    return {
      geometry,
      ...selectionSummary,
    };
  }, [buildSelectionSummary, isLngLatInsideInspectPolygon, isPointInsideInspectGeometry, taskInfo, visibleTrips]);

  const syncInspectMapOverlay = useCallback(() => {
    if (!map.current || !mapLoaded) {
      return;
    }

    const polygonLngLats = inspectSelection?.geometry.polygonLngLats;
    if (!inspectMode || !polygonLngLats || polygonLngLats.length < 4) {
      clearInspectOverlay();
      return;
    }

    const polygonData = {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [polygonLngLats],
        },
      }],
    };

    const existingSource = map.current.getSource(INSPECT_POLYGON_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (existingSource) {
      existingSource.setData(polygonData as any);
    } else {
      map.current.addSource(INSPECT_POLYGON_SOURCE_ID, {
        type: 'geojson',
        data: polygonData as any,
      });
    }

    if (!map.current.getLayer(INSPECT_POLYGON_FILL_LAYER_ID)) {
      map.current.addLayer({
        id: INSPECT_POLYGON_FILL_LAYER_ID,
        type: 'fill',
        source: INSPECT_POLYGON_SOURCE_ID,
        paint: {
          'fill-color': INSPECT_OUTLINE_COLOR,
          'fill-opacity': mode === 'dark' ? 0.2 : 0.14,
        },
      });
    }

    if (!map.current.getLayer(INSPECT_POLYGON_LINE_LAYER_ID)) {
      map.current.addLayer({
        id: INSPECT_POLYGON_LINE_LAYER_ID,
        type: 'line',
        source: INSPECT_POLYGON_SOURCE_ID,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': INSPECT_OUTLINE_COLOR,
          'line-width': 2,
          'line-opacity': 0.98,
        },
      });
    }
  }, [clearInspectOverlay, inspectMode, inspectSelection?.geometry.polygonLngLats, mapLoaded, mode]);

  const clearTouchPointCardHideTimer = useCallback(() => {
    if (tpCardHideTimerRef.current != null) {
      window.clearTimeout(tpCardHideTimerRef.current);
      tpCardHideTimerRef.current = null;
    }
  }, []);

  const scheduleTouchPointCardHide = useCallback(() => {
    clearTouchPointCardHideTimer();
    setHoveredTouchPoint(null);
    setTpInfoPosition(null);
  }, [clearTouchPointCardHideTimer]);

  const addBaseTouchPointMarkersForTrip = useCallback((trip: Trip) => {
    if (!map.current) {
      return;
    }

    const sortedPoints = [...trip.tpDetails].sort((left, right) => left.seqNo - right.seqNo);
    const tripTouchPointMarkers: mapboxgl.Marker[] = [];

    sortedPoints.forEach((touchPoint) => {
      const lngLat = normalizeLngLat(touchPoint.lngLat);
      if (!lngLat) {
        return;
      }

      const markerEl = document.createElement('div');
      markerEl.className = 'touchpoint-marker';

      const tags = touchPoint.slotTags || [];
      const isOrigin = tags.includes('ORIGIN') || touchPoint.name.toLowerCase().includes('origin');
      const isPickup = tags.includes('PICKUP');
      const isDrop = tags.includes('DROP');
      const isUnassigned = tags.includes('UNASSIGNED');
      const hasGeoStatus = Boolean(touchPoint.geoStatus);

      markerEl.style.width = `${DEFAULT_TOUCHPOINT_MARKER_SIZE}px`;
      markerEl.style.height = `${DEFAULT_TOUCHPOINT_MARKER_SIZE}px`;
      markerEl.style.borderRadius = '50%';

      if (hasGeoStatus && !isOrigin && !isPickup) {
        markerEl.style.background = getVerifyPincodeTouchPointColor(touchPoint);
        markerEl.style.border = '1px solid white';
      } else if (isOrigin) {
        markerEl.style.background = '#1e88e5';
        markerEl.style.border = '1px solid #0d47a1';
      } else if (isPickup) {
        markerEl.style.background = '#2e7d32';
        markerEl.style.border = '1px solid white';
      } else if (isDrop) {
        markerEl.style.background = '#ef6c00';
        markerEl.style.border = '1px solid white';
      } else if (isUnassigned) {
        markerEl.style.background = '#6a1b9a';
        markerEl.style.border = '1px solid white';
      } else {
        markerEl.style.background = getAccuracyColor(touchPoint);
        markerEl.style.border = '1px solid white';
      }

      markerEl.style.boxShadow = '0 0 3px rgba(0,0,0,0.24)';
      markerEl.style.cursor = 'pointer';

      markerEl.addEventListener('mouseenter', (event) => {
        clearTouchPointCardHideTimer();
        setHoveredTouchPoint(touchPoint);
        const mouseEvent = event as MouseEvent;
        setTpInfoPosition({ x: mouseEvent.clientX, y: mouseEvent.clientY });
      });

      markerEl.addEventListener('mousemove', (event) => {
        const mouseEvent = event as MouseEvent;
        setTpInfoPosition({ x: mouseEvent.clientX, y: mouseEvent.clientY });
      });

      markerEl.addEventListener('mouseleave', () => {
        scheduleTouchPointCardHide();
      });

      markerEl.addEventListener('click', (event) => {
        event.stopPropagation();
        clearFocusTargetMarker();
        setHoveredTouchPoint(touchPoint);
        setTpInfoPosition(projectTouchPointToViewport(touchPoint));
      });

      const marker = new mapboxgl.Marker({ element: markerEl, anchor: 'center' })
        .setLngLat(lngLat)
        .addTo(map.current!);
      tripTouchPointMarkers.push(marker);
    });

    tripTouchPointMarkersRef.current[trip.name] = tripTouchPointMarkers;
  }, [clearFocusTargetMarker, clearTouchPointCardHideTimer, projectTouchPointToViewport, scheduleTouchPointCardHide]);

  const clearEditOverlay = useCallback(() => {
    removeLayerIfExists(EDIT_POINT_HIT_LAYER_ID);
    removeLayerIfExists(EDIT_POINT_SELECTED_TICK_LAYER_ID);
    removeLayerIfExists(EDIT_POINT_SELECTED_BADGE_LAYER_ID);
    removeLayerIfExists(EDIT_POINT_LABEL_LAYER_ID);
    removeLayerIfExists(EDIT_POINT_SELECTED_RING_LAYER_ID);
    removeLayerIfExists(EDIT_POINT_CIRCLE_LAYER_ID);
    removeLayerIfExists(EDIT_POLYGON_LINE_LAYER_ID);
    removeLayerIfExists(EDIT_POLYGON_FILL_LAYER_ID);
    removeSourceIfExists(EDIT_POINT_SOURCE_ID);
    removeSourceIfExists(EDIT_POLYGON_SOURCE_ID);
    animatedEditOutlineLayerIdsRef.current = [];
  }, [removeLayerIfExists, removeSourceIfExists]);

  const clearTripCardHideTimer = () => {
    if (tripCardHideTimerRef.current != null) {
      window.clearTimeout(tripCardHideTimerRef.current);
      tripCardHideTimerRef.current = null;
    }
  };

  const scheduleTripCardHide = () => {
    clearTripCardHideTimer();
    tripCardHideTimerRef.current = window.setTimeout(() => {
      if (!tripCardHoveredRef.current) {
        setHoveredTripInfo(null);
        setInfoPosition(null);
      }
    }, 140);
  };

  const getVerifyPincodeTouchPointColor = (touchPoint: TouchPoint | null | undefined): string => {
    switch (touchPoint?.geoStatus) {
      case 'corrected':
        return '#7c3aed';
      case 'low_confidence':
        return '#DE3163';
      case 'derived':
        return '#e5ac62';
      default:
        return '#16a34a';
    }
  };

  const hexToRgba = (hex: string, alpha: number): string => {
    const normalized = hex.replace('#', '');
    if (normalized.length !== 6) {
      return `rgba(15,23,42,${alpha})`;
    }
    const value = Number.parseInt(normalized, 16);
    const r = (value >> 16) & 255;
    const g = (value >> 8) & 255;
    const b = value & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const resolveOverlayPosition = useCallback(
    (
      anchor: { x: number; y: number } | null,
      size: { width: number; height: number }
    ): { left: number; top: number } => {
      const fallback = { left: 16, top: 16 };
      if (!anchor || typeof window === 'undefined') return fallback;

      const gap = 12;
      let left = anchor.x + gap;
      let top = anchor.y + gap;

      if (left + size.width > window.innerWidth - gap) {
        left = anchor.x - size.width - gap;
      }
      if (top + size.height > window.innerHeight - gap) {
        top = anchor.y - size.height - gap;
      }

      left = Math.min(Math.max(gap, left), Math.max(gap, window.innerWidth - size.width - gap));
      top = Math.min(Math.max(gap, top), Math.max(gap, window.innerHeight - size.height - gap));
      return { left, top };
    },
    []
  );

  const clampOverlayPositionToMap = useCallback(
    (
      position: { x: number; y: number },
      size: { width: number; height: number }
    ): { x: number; y: number } => {
      const container = mapContainer.current;
      if (!container) {
        return position;
      }

      const maxX = Math.max(MAP_OVERLAY_GAP_PX, container.clientWidth - size.width - MAP_OVERLAY_GAP_PX);
      const maxY = Math.max(MAP_OVERLAY_GAP_PX, container.clientHeight - size.height - MAP_OVERLAY_GAP_PX);

      return {
        x: Math.min(Math.max(MAP_OVERLAY_GAP_PX, position.x), maxX),
        y: Math.min(Math.max(MAP_OVERLAY_GAP_PX, position.y), maxY),
      };
    },
    []
  );

  const getEditCardFallbackPosition = useCallback(
    (size: { width: number; height: number }): { x: number; y: number } => {
      const container = mapContainer.current;
      if (!container) {
        return { x: MAP_OVERLAY_GAP_PX, y: MAP_OVERLAY_GAP_PX };
      }

      return clampOverlayPositionToMap(
        {
          x: container.clientWidth - size.width - EDIT_CARD_FALLBACK_RIGHT_PX,
          y: container.clientHeight - size.height - EDIT_CARD_FALLBACK_BOTTOM_PX,
        },
        size
      );
    },
    [clampOverlayPositionToMap]
  );

  const updateTripInfoCardSize = useCallback((node: HTMLDivElement | null) => {
    tripInfoCardRef.current = node;
    if (node) {
      const rect = node.getBoundingClientRect();
      setTripInfoCardSize({ width: rect.width, height: rect.height });
    }
  }, []);

  const updateEditCardNode = useCallback((node: HTMLDivElement | null) => {
    editCardResizeObserverRef.current?.disconnect();
    editCardResizeObserverRef.current = null;
    editCardRef.current = node;

    if (!node) {
      return;
    }

    const measure = () => {
      const rect = node.getBoundingClientRect();
      setEditCardSize((prev) => (
        prev.width === rect.width && prev.height === rect.height
          ? prev
          : { width: rect.width, height: rect.height }
      ));
    };

    measure();

    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(node);
    editCardResizeObserverRef.current = observer;
  }, []);

  const activeTripInfo = externalTripInfo?.trip ?? hoveredTripInfo;
  const activeTripInfoAnchorClient = React.useMemo(() => {
    if (externalTripInfo?.position) {
      return externalTripInfo.position;
    }
    if (!infoPosition || !mapContainer.current) {
      return null;
    }

    const containerRect = mapContainer.current.getBoundingClientRect();
    return {
      x: containerRect.left + infoPosition.x,
      y: containerRect.top + infoPosition.y,
    };
  }, [externalTripInfo, infoPosition]);
  const tripInfoPlacement = resolveOverlayPosition(activeTripInfoAnchorClient, tripInfoCardSize);
  const editSourceTripInfo = taskInfo?.trips.find((trip) => trip.name === editSourceTrip) ?? null;
  const editSourceTripCenterLngLat = React.useMemo(
    () => normalizeLngLat(editSourceTripInfo?.geoMarkers?.tripCenterLngLat),
    [editSourceTripInfo]
  );
  const editSourceTripPoints = React.useMemo(
    () => editSourceTripInfo?.tpDetails ?? [],
    [editSourceTripInfo]
  );
  const selectedEditTouchPointIds = React.useMemo(
    () => selectedTouchPoints.map((touchPoint) => touchPoint.tpId),
    [selectedTouchPoints]
  );
  const selectedTouchPointIds = React.useMemo(
    () => new Set(selectedEditTouchPointIds),
    [selectedEditTouchPointIds]
  );
  const editTouchPointById = React.useMemo(
    () => new Map(editSourceTripPoints.map((touchPoint) => [touchPoint.tpId, touchPoint])),
    [editSourceTripPoints]
  );
  const manualSelectionSummary = React.useMemo(
    () => (editMode && editSourceTrip ? buildSelectionSummary(selectedTouchPoints, [editSourceTrip]) : null),
    [buildSelectionSummary, editMode, editSourceTrip, selectedTouchPoints]
  );
  const activeSelectionSummary = inspectSelection ?? manualSelectionSummary;
  const activeSelectionKey = React.useMemo(
    () => activeSelectionSummary
      ? activeSelectionSummary.touchPoints.map((touchPoint) => touchPoint.tpId).sort().join('|')
      : '',
    [activeSelectionSummary]
  );
  const selectionTripSummaries = activeSelectionSummary?.tripSummaries ?? [];
  const moveCardVisible = Boolean((inspectSelection || (editMode && editSourceTrip)) && activeSelectionSummary);
  const moveCardUsesAnchoredPosition = Boolean(editMode && editSourceTrip && !inspectSelection);
  const allowTripHoverCards = !inspectMode || moveCardPhase === 'destinations';

  const buildMoveScopeFromTripSummary = useCallback((tripSummary: SelectionTripSummary): MoveScopeSummary => ({
    key: `trip:${tripSummary.tripName}`,
    label: tripSummary.tripName,
    sourceTripNames: [tripSummary.tripName],
    touchPoints: tripSummary.touchPoints,
    pointCount: tripSummary.pointCount,
    weightKg: tripSummary.weightKg,
    volumeCuFt: tripSummary.volumeCuFt,
    estimatedDistanceKm: tripSummary.estimatedDistanceKm,
    estimatedTimeMinutes: tripSummary.estimatedTimeMinutes,
  }), []);

  const buildMoveAllScope = useCallback((selectionSummary: SelectionSummary): MoveScopeSummary => ({
    key: `all:${selectionSummary.touchPoints.map((touchPoint) => touchPoint.tpId).sort().join('|')}`,
    label: selectionSummary.tripSummaries.length > 1 ? 'Move All' : (selectionSummary.tripSummaries[0]?.tripName ?? 'Move All'),
    sourceTripNames: selectionSummary.tripSummaries
      .filter((summary) => summary.pointCount > 0)
      .map((summary) => summary.tripName),
    touchPoints: selectionSummary.touchPoints,
    pointCount: selectionSummary.touchPoints.length,
    weightKg: selectionSummary.totalWeightKg,
    volumeCuFt: selectionSummary.totalVolumeCuFt,
    estimatedDistanceKm: selectionSummary.totalEstimatedDistanceKm,
    estimatedTimeMinutes: selectionSummary.totalEstimatedTimeMinutes,
  }), []);

  const vehicleCapacitySeeds = React.useMemo(() => {
    const seedMap = new Map<string, VehicleCapacitySeed>();
    (taskInfo?.trips ?? []).forEach((trip) => {
      const seed = getVehicleCapacitySeed(trip);
      if (!seed || seedMap.has(seed.vehicleId)) {
        return;
      }
      seedMap.set(seed.vehicleId, seed);
    });
    return Array.from(seedMap.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [getVehicleCapacitySeed, taskInfo]);

  const destinationSourceTripSet = React.useMemo(
    () => new Set(activeMoveScope?.sourceTripNames ?? []),
    [activeMoveScope]
  );

  const existingDestinationOptions = React.useMemo<DestinationOption[]>(() => (
    (taskInfo?.trips ?? [])
      .map((trip) => {
        const vehicleId = String(trip.vehDetails?.vehId ?? '').trim() || trip.name;
        const tripLabel = Number.isFinite(Number(trip.vehTripNo)) && Number(trip.vehTripNo) > 0
          ? `Trip ${trip.vehTripNo}`
          : `Trip ${trip.name}`;
        return {
        id: vehicleId,
        label: tripLabel,
        subtitle: `Vehicle ${vehicleId}`,
        kind: 'existing-trip' as const,
        trip,
        capacities: getTripAvailableCapacity(trip),
        disabled: destinationSourceTripSet.has(trip.name),
      };
      })
      .sort((left, right) => left.label.localeCompare(right.label))
  ), [destinationSourceTripSet, getTripAvailableCapacity, taskInfo]);

  const newTripDestinationOptions = React.useMemo<DestinationOption[]>(() => (
    vehicleCapacitySeeds.map((seed) => ({
      id: seed.driverId,
      label: seed.label,
      subtitle: `New trip via ${seed.driverId}`,
      kind: 'new-trip-vehicle' as const,
      vehicleId: seed.vehicleId,
      capacities: seed.capacities,
      disabled: false,
    }))
  ), [vehicleCapacitySeeds]);

  const hasGeoQualityTouchPoints = (taskInfo?.trips ?? []).some((trip) =>
    visibleTrips.includes(trip.name) && trip.tpDetails.some((touchPoint) => Boolean(touchPoint.geoStatus))
  );

  const resolveEditCardAnchoredPosition = useCallback(
    (size: { width: number; height: number }): { x: number; y: number } => {
      const anchor = projectLngLatToContainer(editSourceTripCenterLngLat);
      if (!anchor) {
        return getEditCardFallbackPosition(size);
      }

      const container = mapContainer.current;
      if (!container) {
        return getEditCardFallbackPosition(size);
      }

      let x = anchor.x + EDIT_CARD_ANCHOR_OFFSET_PX;
      let y = anchor.y - (size.height / 2);

      if (x + size.width > container.clientWidth - MAP_OVERLAY_GAP_PX) {
        x = anchor.x - size.width - EDIT_CARD_ANCHOR_OFFSET_PX;
      }

      return clampOverlayPositionToMap({ x, y }, size);
    },
    [clampOverlayPositionToMap, editSourceTripCenterLngLat, getEditCardFallbackPosition, projectLngLatToContainer]
  );

  const isInteractiveEditCardTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(target.closest([
      'button',
      'a',
      'input',
      'textarea',
      'select',
      'option',
      '[role="button"]',
      '[role="combobox"]',
      '[role="listbox"]',
      '[role="option"]',
      '.MuiButtonBase-root',
      '.MuiFormControl-root',
      '.MuiInputBase-root',
      '.MuiInputLabel-root',
    ].join(',')));
  }, []);

  const handleEditCardPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!moveCardVisible || isInteractiveEditCardTarget(event.target)) {
      return;
    }

    const origin = editCardPosition ?? (
      moveCardUsesAnchoredPosition
        ? resolveEditCardAnchoredPosition(editCardSizeRef.current)
        : getEditCardFallbackPosition(editCardSizeRef.current)
    );
    event.preventDefault();
    event.stopPropagation();
    editCardDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: origin.x,
      originY: origin.y,
      dragged: false,
    };
  }, [
    editCardPosition,
    getEditCardFallbackPosition,
    isInteractiveEditCardTarget,
    moveCardUsesAnchoredPosition,
    moveCardVisible,
    resolveEditCardAnchoredPosition,
  ]);

  useEffect(() => {
    activeMoveScopeRef.current = activeMoveScope;
  }, [activeMoveScope]);

  useEffect(() => {
    moveCardPhaseRef.current = moveCardPhase;
  }, [moveCardPhase]);

  useEffect(() => {
    editSubmittingRef.current = editSubmitting;
  }, [editSubmitting]);

  useEffect(() => {
    return () => {
      editCardResizeObserverRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    editCardSizeRef.current = editCardSize;
  }, [editCardSize]);

  useEffect(() => {
    setShowRoutes(defaultShowRoutes);
  }, [defaultShowRoutes, taskInfo?.taskId]);

  useEffect(() => {
    if (inspectAvailable && !editMode && !geoCorrectionTouchPoint) {
      return;
    }
    if (!inspectMode) {
      return;
    }
    setInspectMode(false);
    clearInspectState();
  }, [clearInspectState, editMode, geoCorrectionTouchPoint, inspectAvailable, inspectMode]);

  useEffect(() => {
    if (!inspectMode) {
      clearInspectState();
      return;
    }

    setHoveredTripInfo(null);
    setInfoPosition(null);
    setHoveredTouchPoint(null);
    setTpInfoPosition(null);
    setExternalTpInfo(null);
    onClearExternalTouchPoint?.();
  }, [clearInspectState, inspectMode, onClearExternalTouchPoint]);

  useEffect(() => {
    setMoveCardPhase('summary');
    setActiveMoveScope(null);
    setDestinationSearchTerm('');
  }, [activeSelectionKey, editSourceTrip, inspectSelection?.geometry]);

  useEffect(() => {
    if (!inspectMode || !inspectSelection?.geometry) {
      return;
    }

    const nextSelection = buildInspectSelection(inspectSelection.geometry);
    setInspectSelection(nextSelection);
  }, [buildInspectSelection, inspectMode, inspectSelection?.geometry, taskInfo, visibleTrips]);

  useEffect(() => {
    if (!inspectMode) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      setInspectMode(false);
      clearInspectState();
      setMoveCardPhase('summary');
      setActiveMoveScope(null);
      setDestinationSearchTerm('');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [clearInspectState, inspectMode]);

  useEffect(() => {
    if (!moveCardVisible) {
      editCardDragRef.current = null;
      setEditCardDragging(false);
      setEditCardPositionMode('anchored');
      setEditCardPosition(null);
      return;
    }

    setHoveredTripInfo(null);
    setInfoPosition(null);
    editCardDragRef.current = null;
    setEditCardDragging(false);
    if (moveCardUsesAnchoredPosition) {
      setEditCardPositionMode('anchored');
      setEditCardPosition(resolveEditCardAnchoredPosition(editCardSizeRef.current));
      return;
    }
    setEditCardPositionMode('manual');
    setEditCardPosition((prev) => prev ?? getEditCardFallbackPosition(editCardSizeRef.current));
  }, [
    getEditCardFallbackPosition,
    moveCardUsesAnchoredPosition,
    moveCardVisible,
    resolveEditCardAnchoredPosition,
    taskInfo?.taskId,
  ]);

  useEffect(() => {
    if (!moveCardVisible) {
      return;
    }

    if (moveCardUsesAnchoredPosition && editCardPositionMode === 'anchored') {
      setEditCardPosition(resolveEditCardAnchoredPosition(editCardSize));
      return;
    }

    setEditCardPosition((prev) => (
      prev ? clampOverlayPositionToMap(prev, editCardSize) : getEditCardFallbackPosition(editCardSize)
    ));
  }, [
    clampOverlayPositionToMap,
    editCardPositionMode,
    editCardSize,
    getEditCardFallbackPosition,
    moveCardUsesAnchoredPosition,
    moveCardVisible,
    resolveEditCardAnchoredPosition,
  ]);

  useEffect(() => {
    if (
      !map.current ||
      !mapLoaded ||
      !moveCardVisible ||
      !moveCardUsesAnchoredPosition ||
      editCardPositionMode !== 'anchored'
    ) {
      return;
    }

    const syncEditCardPosition = () => {
      setEditCardPosition(resolveEditCardAnchoredPosition(editCardSizeRef.current));
    };

    map.current.on('move', syncEditCardPosition);
    map.current.on('resize', syncEditCardPosition);

    return () => {
      map.current?.off('move', syncEditCardPosition);
      map.current?.off('resize', syncEditCardPosition);
    };
  }, [editCardPositionMode, mapLoaded, moveCardUsesAnchoredPosition, moveCardVisible, resolveEditCardAnchoredPosition]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const dragState = editCardDragRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      if (!dragState.dragged) {
        if (Math.hypot(deltaX, deltaY) < EDIT_CARD_DRAG_THRESHOLD_PX) {
          return;
        }
        dragState.dragged = true;
        setEditCardPositionMode('manual');
        setEditCardDragging(true);
      }

      setEditCardPosition(
        clampOverlayPositionToMap(
          {
            x: dragState.originX + deltaX,
            y: dragState.originY + deltaY,
          },
          editCardSizeRef.current
        )
      );
    };

    const clearDragState = (event: PointerEvent) => {
      if (editCardDragRef.current?.pointerId !== event.pointerId) {
        return;
      }
      editCardDragRef.current = null;
      setEditCardDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', clearDragState);
    window.addEventListener('pointercancel', clearDragState);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', clearDragState);
      window.removeEventListener('pointercancel', clearDragState);
    };
  }, [clampOverlayPositionToMap]);

  const resolveFitPadding = () => (
    typeof fitPadding === 'number' || !fitPadding
      ? (fitPadding as number | undefined) ?? 50
      : fitPadding
  );

  const getMapCameraSize = (): { width: number; height: number } | null => {
    const canvas = map.current?.getCanvas();
    const canvasWidth = canvas?.clientWidth ?? 0;
    const canvasHeight = canvas?.clientHeight ?? 0;
    const containerRect = mapContainer.current?.getBoundingClientRect();
    const width = canvasWidth || containerRect?.width || 0;
    const height = canvasHeight || containerRect?.height || 0;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width < MIN_CAMERA_VIEWPORT_PX || height < MIN_CAMERA_VIEWPORT_PX) {
      return null;
    }
    return { width, height };
  };

  const resetMapCameraState = () => {
    if (!map.current) {
      return;
    }

    map.current.stop();
    map.current.setPadding({
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    });
  };

  const resolveSafeFitPadding = (padding: number | FocusPadding | undefined): FocusPadding | undefined => {
    const cameraSize = getMapCameraSize();
    if (!cameraSize) {
      return undefined;
    }

    if (typeof padding === 'number') {
      const safePadding = Number.isFinite(padding) ? Math.max(0, padding) : 0;
      return clampFocusPadding({
        top: safePadding,
        bottom: safePadding,
        left: safePadding,
        right: safePadding,
      }, cameraSize.width, cameraSize.height);
    }

    if (!padding) {
      return undefined;
    }

    return clampFocusPadding(padding, cameraSize.width, cameraSize.height);
  };

  const getDistinctLngLatCount = (coords: [number, number][]): number => {
    const seen = new Set<string>();
    coords.forEach(([lng, lat]) => {
      seen.add(`${lng.toFixed(7)},${lat.toFixed(7)}`);
    });
    return seen.size;
  };

  const fitMapToCoords = (
    coords: [number, number][],
    options?: { maxZoom?: number; singlePointZoom?: number; padding?: number | FocusPadding }
  ) => {
    const safeCoords = coords
      .map((coordsPair) => normalizeLngLat(coordsPair))
      .filter((coordsPair): coordsPair is [number, number] => !!coordsPair);
    const cameraSize = getMapCameraSize();
    if (!map.current || safeCoords.length === 0 || !cameraSize) {
      return;
    }
    const padding = resolveSafeFitPadding(options?.padding ?? resolveFitPadding());
    resetMapCameraState();

    if (safeCoords.length >= 2 && getDistinctLngLatCount(safeCoords) >= 2) {
      const bounds = new mapboxgl.LngLatBounds();
      safeCoords.forEach((coordsPair) => bounds.extend(coordsPair));
      try {
        map.current.fitBounds(bounds, {
          padding,
          maxZoom: options?.maxZoom ?? 15,
          retainPadding: false,
        } as any);
      } catch (error) {
        map.current.easeTo({
          center: safeCoords[0],
          zoom: Math.max(map.current.getZoom(), options?.singlePointZoom ?? 12),
          padding,
          duration: 720,
          essential: true,
          retainPadding: false,
        } as any);
      }
      return;
    }

    map.current.easeTo({
      center: safeCoords[0],
      zoom: Math.max(map.current.getZoom(), options?.singlePointZoom ?? 12),
      padding,
      duration: 720,
      essential: true,
      retainPadding: false,
    } as any);
  };

  const getTripFocusLngLats = (trip: Trip): [number, number][] => {
    const routeCoords = Array.isArray(trip.geoMarkers?.tripPolylineLngLats)
      ? trip.geoMarkers.tripPolylineLngLats
          .map((coords) => normalizeLngLat(coords))
          .filter((coords): coords is [number, number] => !!coords)
      : [];
    if (routeCoords.length >= 2) {
      return routeCoords;
    }

    const polygonCoords = Array.isArray(trip.geoMarkers?.tripBoundaryLngLats)
      ? trip.geoMarkers.tripBoundaryLngLats
          .map((coords) => normalizeLngLat(coords))
          .filter((coords): coords is [number, number] => !!coords)
      : [];
    if (polygonCoords.length >= 3) {
      return polygonCoords;
    }

    const touchPointCoords = trip.tpDetails
      .map((touchPoint) => normalizeLngLat(touchPoint.lngLat))
      .filter((coords): coords is [number, number] => !!coords);
    if (touchPointCoords.length > 0) {
      return touchPointCoords;
    }

    const tripCenter = normalizeLngLat(trip.geoMarkers?.tripCenterLngLat);
    return tripCenter ? [tripCenter] : [];
  };

  // Compute all coordinates to fit for currently visible trips
  const getVisibleLngLats = (): [number, number][] => {
    if (!taskInfo) return [];

    const trips = taskInfo.trips.filter((t) => visibleTrips.includes(t.name));

    const polygonCoords = showPolygons
      ? trips.flatMap((t) =>
          (t.geoMarkers?.tripBoundaryLngLats || [])
            .map((coords) => normalizeLngLat(coords))
            .filter((coords): coords is [number, number] => !!coords)
        )
      : [];

    const tpCoords = trips.flatMap((t) =>
      t.tpDetails
        .map((tp) => normalizeLngLat(tp.lngLat))
        .filter((coords): coords is [number, number] => !!coords)
    );

    let coords = polygonCoords.length > 0 ? polygonCoords : tpCoords;

    const source = normalizeLngLat((taskInfo as any)?.config?.sourceLngLat);
    if (source) {
      coords = [...coords, source];
    }

    return coords
      .map((coordsPair) => normalizeLngLat(coordsPair))
      .filter((coordsPair): coordsPair is [number, number] => !!coordsPair);
  };

  const fitMapToVisible = () => {
    fitMapToCoords(getVisibleLngLats());
  };

  const fitMapToTrip = (tripName: string, options?: { focusAware?: boolean }) => {
    if (!taskInfo) {
      return;
    }

    const trip = taskInfo.trips.find((candidate) => candidate.name === tripName);
    if (!trip) {
      return;
    }

    fitMapToCoords(getTripFocusLngLats(trip), {
      maxZoom: 14,
      singlePointZoom: 11.5,
      padding: options?.focusAware ? getFocusPadding() : undefined,
    });
  };

  const exitInspectMode = () => {
    setInspectMode(false);
    clearInspectState();
    setMoveCardPhase('summary');
    setActiveMoveScope(null);
    setDestinationSearchTerm('');
  };

  const handleInspectToolSelect = (tool: InspectTool) => {
    if (!inspectAvailable || editMode || geoCorrectionTouchPoint) {
      return;
    }

    if (inspectMode && inspectTool === tool) {
      exitInspectMode();
      return;
    }

    setInspectTool(tool);
    clearInspectState();
    setInspectMode(true);
  };

  const handleInspectOverlayPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!inspectMode) {
      return;
    }

    const origin = getRelativeClientPoint(event.clientX, event.clientY);
    if (!origin) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    inspectPointerRef.current = {
      pointerId: event.pointerId,
      origin,
    };
    setInspectSelection(null);
    setInspectDraftGeometry(buildInspectGeometry(inspectTool, origin, origin));
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleInspectOverlayPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const pointerState = inspectPointerRef.current;
    if (!inspectMode || !pointerState || pointerState.pointerId !== event.pointerId) {
      return;
    }

    const currentPoint = getRelativeClientPoint(event.clientX, event.clientY);
    if (!currentPoint) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setInspectDraftGeometry(buildInspectGeometry(inspectTool, pointerState.origin, currentPoint));
  };

  const finalizeInspectSelection = (
    event: React.PointerEvent<HTMLDivElement>,
    preserveSelection: boolean
  ) => {
    const pointerState = inspectPointerRef.current;
    if (!pointerState || pointerState.pointerId !== event.pointerId) {
      return;
    }

    const currentPoint = getRelativeClientPoint(event.clientX, event.clientY) ?? pointerState.origin;
    const geometry = buildInspectGeometry(inspectTool, pointerState.origin, currentPoint, true);

    inspectPointerRef.current = null;
    setInspectDraftGeometry(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (!preserveSelection) {
      return;
    }

    const nextSelection = buildInspectSelection(geometry);
    setInspectSelection(nextSelection);
  };

  const handleInspectOverlayPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!inspectMode) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    finalizeInspectSelection(event, true);
  };

  const handleInspectOverlayPointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    finalizeInspectSelection(event, false);
  };

  const handleDismissMoveCard = () => {
    setMoveCardPhase('summary');
    setActiveMoveScope(null);
    setDestinationSearchTerm('');
    if (inspectSelection) {
      exitInspectMode();
      return;
    }
    onCancelEdit();
  };

  const handleMoveScopeSelect = useCallback((scope: MoveScopeSummary) => {
    if (!scope.touchPoints.length) {
      return;
    }
    setActiveMoveScope(scope);
    setMoveCardPhase('destinations');
    setDestinationSearchTerm('');
  }, []);

  const handleOpenNewTripChooser = useCallback(() => {
    if (!activeMoveScopeRef.current?.touchPoints.length) {
      return;
    }
    setMoveCardPhase('new-trip');
    setDestinationSearchTerm('');
  }, []);

  const handleBackToDestinationTrips = useCallback(() => {
    setMoveCardPhase('destinations');
    setDestinationSearchTerm('');
  }, []);

  const submitMoveScope = useCallback(async (destinationTripId: string) => {
    const scope = activeMoveScopeRef.current;
    if (!scope || !scope.touchPoints.length || editSubmittingRef.current) {
      return;
    }

    const result = await onSubmitEdit({
      touchPointIds: scope.touchPoints.map((touchPoint) => touchPoint.tpId),
      destinationTripId,
    });

    if (result === false) {
      return;
    }

    setMoveCardPhase('summary');
    setActiveMoveScope(null);
    setDestinationSearchTerm('');
    if (inspectSelection) {
      setInspectMode(false);
      clearInspectState();
    }
  }, [clearInspectState, inspectSelection, onSubmitEdit]);

  useEffect(() => {
    submitMoveScopeRef.current = (destinationTripId: string) => {
      void submitMoveScope(destinationTripId);
    };
  }, [submitMoveScope]);

  const filteredDestinationOptions = React.useMemo(() => {
    const source = moveCardPhase === 'new-trip'
      ? newTripDestinationOptions
      : existingDestinationOptions;
    const term = destinationSearchTerm.trim().toLowerCase();
    if (!term) {
      return source;
    }
    return source.filter((option) => (
      option.label.toLowerCase().includes(term) ||
      option.subtitle.toLowerCase().includes(term)
    ));
  }, [destinationSearchTerm, existingDestinationOptions, moveCardPhase, newTripDestinationOptions]);


  // Initialize map when component mounts
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    // Create the map instance
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mode === 'dark' ? DARK_MAP_STYLE : LIGHT_MAP_STYLE,
      center: MAP_DEFAULT_CENTER,
      zoom: MAP_DEFAULT_ZOOM,
    });
    // Track current style URL to avoid unnecessary reloads
    (map.current as any).__currentStyleUrl = mode === 'dark' ? DARK_MAP_STYLE : LIGHT_MAP_STYLE;

    // Add navigation control
    map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

    // Apply max bounds if provided
    if (maxBounds && Array.isArray(maxBounds) && maxBounds.length === 2) {
      map.current.setMaxBounds(maxBounds as unknown as mapboxgl.LngLatBoundsLike);
    }

    // Set up event listeners
    map.current.on('load', () => {
      setMapLoaded(true);
    });

    // Cleanup on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // Map initialization is one-time; bounds updates are handled separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Update max bounds if they change at runtime
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (maxBounds && Array.isArray(maxBounds) && maxBounds.length === 2) {
      map.current.setMaxBounds(maxBounds as unknown as mapboxgl.LngLatBoundsLike);
    } else {
      // Remove bounds restriction
      (map.current as any).setMaxBounds(null);
    }
  }, [maxBounds, mapLoaded]);

  // Update map style when theme mode changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    
    const newStyle = mode === 'dark' ? DARK_MAP_STYLE : LIGHT_MAP_STYLE;

    // Only change style if actually different to prevent data loss
    const currentStyleUrl = (map.current as any).__currentStyleUrl;
    if (currentStyleUrl === newStyle) return;
    (map.current as any).__currentStyleUrl = newStyle;
    map.current.setStyle(newStyle);

    // After style reloads, re-add layers/markers and re-fit the map
    const handleStyleLoad = () => {
      // Remove our existing markers and clear cache
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      Object.values(tripTouchPointMarkersRef.current).forEach((tripMarkers) => {
        tripMarkers.forEach((marker) => marker.remove());
      });
      tripTouchPointMarkersRef.current = {};
      clearEditOverlay();
      clearInspectOverlay();

      addTripLayers();
      syncEditOverlayRef.current();
      syncInspectOverlayRef.current();
      fitMapToVisible();
    };

    map.current.once('style.load', handleStyleLoad);
    // Style swaps intentionally reuse the latest layer helpers without re-registering the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearEditOverlay, clearInspectOverlay, mapLoaded, mode]);

  useEffect(() => {
    if (!externalTouchPoint) {
      setExternalTpInfo(null);
      return;
    }
    setHoveredTouchPoint(null);
    setTpInfoPosition(null);
    if (externalTouchPointAnchorClient) {
      setExternalTpInfo({
        tp: externalTouchPoint,
        position: externalTouchPointAnchorClient,
        positionMode: 'viewport',
      });
      return;
    }
    const projected = projectTouchPointToViewport(externalTouchPoint);
    if (!projected) {
      setExternalTpInfo(null);
      return;
    }
    setExternalTpInfo({
      tp: externalTouchPoint,
      position: projected,
      positionMode: 'map',
    });
  }, [externalTouchPoint, externalTouchPointAnchorClient, mapLoaded, projectTouchPointToViewport]);

  useEffect(() => {
    if (!externalTripHover) {
      setExternalTripInfo(null);
      return;
    }

    const trip = taskInfo?.trips.find((candidate) => candidate.name === externalTripHover.tripName);
    const position = externalTripHover.anchorClientPosition;
    if (!trip || !position) {
      setExternalTripInfo(null);
      return;
    }

    setHoveredTripInfo(null);
    setInfoPosition(null);
    setExternalTripInfo({ trip, position });
  }, [externalTripHover, taskInfo]);

  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return;
    }

    const syncMapAnchoredTouchPointPreview = () => {
      setExternalTpInfo((prev) => {
        if (!prev || prev.positionMode !== 'map') {
          return prev;
        }
        const projected = projectTouchPointToViewport(prev.tp);
        if (!projected) {
          return null;
        }
        if (prev.position.x === projected.x && prev.position.y === projected.y) {
          return prev;
        }
        return {
          ...prev,
          position: projected,
        };
      });
    };

    map.current.on('move', syncMapAnchoredTouchPointPreview);
    map.current.on('resize', syncMapAnchoredTouchPointPreview);

    return () => {
      map.current?.off('move', syncMapAnchoredTouchPointPreview);
      map.current?.off('resize', syncMapAnchoredTouchPointPreview);
    };
  }, [mapLoaded, projectTouchPointToViewport]);

  // Function to add trip layers to map
  const addTripLayers = () => {
    if (!map.current || !mapLoaded || !taskInfo) return;

    // Only render layers/markers for visible trips
    const visibleTripList = taskInfo.trips.filter((t) => visibleTrips.includes(t.name));

    // For each visible trip, add polygon layer (if enabled) and markers
    visibleTripList.forEach((trip) => {
      const tripId = `trip-${trip.name}`;
      const executionTrip = executionOverlay?.trips.find((candidate) => candidate.id === trip.name);
      const colorIndex = taskInfo.trips.findIndex((t) => t.name === trip.name);
      const baseColor = COLORS.tripColors[colorIndex % COLORS.tripColors.length];
      const isUnallocated = (trip.tripMeta?.status || trip.slotTags[0]) === 'UNALLOCATED';
      const isVerifyGroupTrip = (trip.tripMeta?.status || '').toUpperCase() === 'VERIFY_GROUP';
      const isActiveTrip = !editMode && activeTripName != null && trip.name === activeTripName;
      const dimInactiveTrips = !editMode && activeTripName != null && trip.name !== activeTripName;
      const color = isUnallocated ? '#9e9e9e' : baseColor;
      const sortedPoints = [...trip.tpDetails].sort((a, b) => a.seqNo - b.seqNo);
      
      // Add polygon source for the trip only when showPolygons is true
      if (
        showPolygons &&
        trip.geoMarkers.tripBoundaryLngLats.length > 2 &&
        map.current
      ) {
        // Create a GeoJSON source for the polygon
        map.current.addSource(tripId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: { name: trip.name },
            geometry: {
              type: 'Polygon',
              coordinates: [trip.geoMarkers.tripBoundaryLngLats]
            }
          }
        });
        
        // Add the polygon layer
        map.current.addLayer({
          id: `${tripId}-fill`,
          type: 'fill',
          source: tripId,
          layout: {},
          paint: {
            'fill-color': color,
            'fill-opacity': isActiveTrip ? 0.36 : dimInactiveTrips ? 0.12 : 0.22,
          }
        });
        
        // Add the polygon outline
        map.current.addLayer({
          id: `${tripId}-line`,
          type: 'line',
          source: tripId,
          layout: {},
          paint: {
            'line-color': color,
            'line-width': isActiveTrip ? 3 : 2,
            'line-opacity': dimInactiveTrips ? 0.45 : 0.95,
          },
        });
      }
      
      if (executionTrip && executionOverlay && map.current) {
        const hasActiveTrip = executionOverlay.trips.some((candidate) => candidate.active);
        const isEmphasized = executionTrip.active || !hasActiveTrip;
        const inactiveOpacity = isEmphasized ? 1 : 0.3;
        if (executionOverlay.layers.plannedRoutes && executionTrip.plannedRoute.length > 0) {
          const sourceId = `${tripId}-execution-planned`;
          map.current.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: { type: 'MultiLineString', coordinates: executionTrip.plannedRoute },
            },
          });
          map.current.addLayer({
            id: `${sourceId}-line`,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': mode === 'dark' ? '#cbd5e1' : '#64748b',
              'line-width': isEmphasized ? 2.6 : 2,
              'line-opacity': 0.8 * inactiveOpacity,
              'line-dasharray': [2, 2],
            },
          });
        }
        if (executionOverlay.layers.actualRoutes && executionTrip.actualRoute.length >= 2) {
          const sourceId = `${tripId}-execution-actual`;
          map.current.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates: executionTrip.actualRoute },
            },
          });
          map.current.addLayer({
            id: `${sourceId}-line`,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': executionTrip.color,
              'line-width': executionTrip.active ? 5 : 3.8,
              'line-opacity': 0.96 * inactiveOpacity,
            },
          });
        }
      }
      // Prefer the sparse route-geometry artifact. Existing trip polylines remain a
      // compatibility fallback for tenants that have not adopted the artifact yet.
      else if (allowRouteLines && showRoutes && map.current) {
        const artifactSegments = routeGeometryByTrip?.[trip.name];
        const rawPolyline = Array.isArray(trip.geoMarkers?.tripPolylineLngLats)
          ? trip.geoMarkers.tripPolylineLngLats
              .map((coords: any) => normalizeLngLat(coords))
              .filter((coords): coords is [number, number] => !!coords)
          : [];
        const fallbackRoute = isUnallocated
          ? sortedPoints
              .map(tp => normalizeLngLat(tp.lngLat))
              .filter((coords): coords is [number, number] => !!coords)
          : [];
        const routeCoords = rawPolyline.length >= 2 ? rawPolyline : fallbackRoute;

        if (artifactSegments) {
          if (artifactSegments.exact.length > 0) {
            const sourceId = `${tripId}-route-exact`;
            map.current.addSource(sourceId, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'MultiLineString',
                  coordinates: artifactSegments.exact,
                },
              },
            });
            map.current.addLayer({
              id: `${tripId}-route-exact-line`,
              type: 'line',
              source: sourceId,
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: {
                'line-color': color,
                'line-width': isActiveTrip ? 4 : 3,
                'line-opacity': dimInactiveTrips ? 0.35 : 0.9,
              },
            });
          }
          if (artifactSegments.fallback.length > 0) {
            const sourceId = `${tripId}-route-fallback`;
            map.current.addSource(sourceId, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'MultiLineString',
                  coordinates: artifactSegments.fallback,
                },
              },
            });
            map.current.addLayer({
              id: `${tripId}-route-fallback-line`,
              type: 'line',
              source: sourceId,
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: {
                'line-color': color,
                'line-width': 2,
                'line-opacity': dimInactiveTrips ? 0.3 : 0.68,
                'line-dasharray': [2, 2],
              },
            });
          }
        } else if (routeCoords.length >= 2) {
          const sourceId = `${tripId}-route`;
          const layerId = `${tripId}-route-line`;

          map.current.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: routeCoords,
              },
            },
          });

          map.current.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': color,
              'line-width': isActiveTrip ? 4 : 3,
              'line-opacity': dimInactiveTrips ? 0.35 : 0.85,
            },
          });
        }
      }

      const center = normalizeLngLat(trip.geoMarkers?.tripCenterLngLat);
      if (isVerifyGroupTrip && center && map.current) {
        const labelBorder = hexToRgba(color, isActiveTrip ? 0.42 : 0.26);
        const labelEl = document.createElement('div');
        labelEl.className = 'pincode-cluster-label';
        labelEl.style.padding = '5px 10px';
        labelEl.style.borderRadius = '999px';
        labelEl.style.background = hexToRgba(color, isActiveTrip ? 0.3 : 0.22);
        labelEl.style.color = '#0f172a';
        labelEl.style.fontSize = '11px';
        labelEl.style.fontWeight = '700';
        labelEl.style.letterSpacing = '0.24px';
        labelEl.style.border = `1px solid ${labelBorder}`;
        labelEl.style.boxShadow = 'none';
        labelEl.style.cursor = 'pointer';
        labelEl.style.opacity = dimInactiveTrips ? '0.48' : '1';
        labelEl.textContent = String(trip.tripMeta?.extra?.pinCode ?? trip.slotTags?.[0] ?? trip.name);

        const labelMarker = new mapboxgl.Marker({ element: labelEl, anchor: 'center' })
          .setLngLat(center)
          .addTo(map.current);
        markersRef.current.push(labelMarker);

        labelEl.addEventListener('mouseenter', (e) => {
          clearTripCardHideTimer();
          setHoveredTripInfo(trip);
          if (mapContainer.current) {
            const rect = mapContainer.current.getBoundingClientRect();
            const me = e as MouseEvent;
            setInfoPosition({ x: me.clientX - rect.left, y: me.clientY - rect.top });
          }
        });
        labelEl.addEventListener('mousemove', (e) => {
          clearTripCardHideTimer();
          if (mapContainer.current) {
            const rect = mapContainer.current.getBoundingClientRect();
            const me = e as MouseEvent;
            setInfoPosition({ x: me.clientX - rect.left, y: me.clientY - rect.top });
          }
        });
        labelEl.addEventListener('mouseleave', () => {
          scheduleTripCardHide();
        });
        labelEl.addEventListener('click', (event) => {
          event.stopPropagation();
          clearFocusTargetMarker();
          if (map.current) {
            setHoveredTripInfo(trip);
            setInfoPosition(map.current.project(center));
          }
        });
      }

      if (showTripVehicleMarkers && center && map.current) {
        const vehicleEl = document.createElement('div');
        vehicleEl.className = 'vehicle-marker';
        vehicleEl.style.width = '22px';
        vehicleEl.style.height = '22px';
        vehicleEl.style.borderRadius = '8px';
        vehicleEl.style.background = isUnallocated ? '#6b7280' : '#0f4c81';
        vehicleEl.style.border = '2px solid #f8fafc';
        vehicleEl.style.display = 'flex';
        vehicleEl.style.alignItems = 'center';
        vehicleEl.style.justifyContent = 'center';
        vehicleEl.style.color = 'white';
        vehicleEl.style.fontSize = '12px';
        vehicleEl.style.fontWeight = 'bold';
        vehicleEl.style.cursor = 'pointer';
        vehicleEl.style.boxShadow = '0 2px 10px rgba(15,23,42,0.28)';
        vehicleEl.style.opacity = dimInactiveTrips ? '0.4' : '1';
        vehicleEl.textContent = '🚚';

        const vehicleMarker = new mapboxgl.Marker({ element: vehicleEl })
          .setLngLat(center)
          .addTo(map.current);
        markersRef.current.push(vehicleMarker);

        vehicleEl.addEventListener('mouseenter', (e) => {
          clearTripCardHideTimer();
          setHoveredTripInfo(trip);
          if (mapContainer.current) {
            const rect = mapContainer.current.getBoundingClientRect();
            const me = e as MouseEvent;
            setInfoPosition({ x: me.clientX - rect.left, y: me.clientY - rect.top });
          }
        });
        vehicleEl.addEventListener('mousemove', (e) => {
          clearTripCardHideTimer();
          if (mapContainer.current) {
            const rect = mapContainer.current.getBoundingClientRect();
            const me = e as MouseEvent;
            setInfoPosition({ x: me.clientX - rect.left, y: me.clientY - rect.top });
          }
        });
        vehicleEl.addEventListener('mouseleave', () => {
          scheduleTripCardHide();
        });
        vehicleEl.addEventListener('click', (event) => {
          clearFocusTargetMarker();
          if (
            !editEnabled ||
            moveCardPhaseRef.current !== 'destinations' ||
            !activeMoveScopeRef.current ||
            editSubmittingRef.current ||
            activeMoveScopeRef.current.sourceTripNames.includes(trip.name)
          ) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          const destinationId = String(trip.vehDetails?.vehId ?? '').trim() || trip.name;
          submitMoveScopeRef.current(destinationId);
        });
      }

      if (executionTrip && executionOverlay && map.current) {
        const hasActiveTrip = executionOverlay.trips.some((candidate) => candidate.active);
        const markerOpacity = executionTrip.active || !hasActiveTrip ? '1' : '0.34';
        const stateColor: Record<ExecutionMapPointState, string> = {
          planned: mode === 'dark' ? '#cbd5e1' : '#64748b',
          visited: executionTrip.color,
          rto: '#dc2626',
          current: '#0f766e',
          next: '#d97706',
          'out-of-sequence': '#ea580c',
          unplanned: '#7c3aed',
        };

        if (executionOverlay.layers.points) {
          executionTrip.activities.forEach((activity) => {
            const markerEl = document.createElement('div');
            const selected = executionOverlay.activeActivityId === activity.id;
            const isDiamond = activity.state === 'unplanned';
            const isPending = activity.state === 'planned' || activity.state === 'next';
            markerEl.className = `execution-point-marker execution-point-marker--${activity.state}`;
            markerEl.style.width = activity.state === 'current' ? '24px' : '20px';
            markerEl.style.height = activity.state === 'current' ? '24px' : '20px';
            markerEl.style.boxSizing = 'border-box';
            markerEl.style.borderRadius = isDiamond ? '4px' : '50%';
            markerEl.style.transform = isDiamond ? 'rotate(45deg)' : '';
            markerEl.style.background = isPending ? (mode === 'dark' ? '#172033' : '#fff') : stateColor[activity.state];
            markerEl.style.border = `2px solid ${stateColor[activity.state]}`;
            markerEl.style.color = isPending ? stateColor[activity.state] : '#fff';
            markerEl.style.display = 'flex';
            markerEl.style.alignItems = 'center';
            markerEl.style.justifyContent = 'center';
            markerEl.style.fontSize = '9px';
            markerEl.style.fontWeight = '800';
            markerEl.style.cursor = 'pointer';
            markerEl.style.opacity = markerOpacity;
            markerEl.style.boxShadow = selected
              ? `0 0 0 4px ${hexToRgba(executionTrip.color, 0.28)}, 0 2px 8px rgba(15,23,42,0.34)`
              : activity.state === 'current'
                ? `0 0 0 5px ${hexToRgba(stateColor.current, 0.22)}, 0 2px 8px rgba(15,23,42,0.28)`
                : '0 2px 6px rgba(15,23,42,0.26)';
            markerEl.dataset.defaultBoxShadow = markerEl.style.boxShadow;
            if (executionHoveredActivityId === activity.id) {
              markerEl.style.boxShadow = '0 0 0 5px rgba(37,99,235,0.28), 0 3px 10px rgba(15,23,42,0.38)';
            }
            markerEl.title = `${activity.title} · ${activity.state.replaceAll('-', ' ')}`;
            if (executionOverlay.layers.sequences) {
              const sequence = activity.actualSequence ?? activity.plannedSequence;
              markerEl.textContent = sequence == null ? '!' : String(sequence);
            }
            if (isDiamond && markerEl.textContent) {
              const label = document.createElement('span');
              label.textContent = markerEl.textContent;
              label.style.transform = 'rotate(-45deg)';
              markerEl.textContent = '';
              markerEl.appendChild(label);
            }
            if (executionOverlay.layers.exceptions && activity.exceptionTags.length > 0) {
              markerEl.style.outline = '2px solid rgba(245,158,11,0.8)';
              markerEl.style.outlineOffset = '2px';
            }
            markerEl.addEventListener('mouseenter', () => {
              markerEl.style.opacity = '1';
              markerEl.style.zIndex = '4';
              onExecutionActivityHover?.(executionTrip.id, activity.id);
            });
            markerEl.addEventListener('mouseleave', () => {
              markerEl.style.opacity = markerOpacity;
              markerEl.style.zIndex = '';
              onExecutionActivityHover?.(null, null);
            });
            markerEl.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              onExecutionActivityClick?.(executionTrip.id, activity.id);
            });
            const marker = new mapboxgl.Marker({ element: markerEl, anchor: 'center' })
              .setLngLat(activity.location)
              .addTo(map.current!);
            markersRef.current.push(marker);
            executionMarkerElementsRef.current[activity.id] = markerEl;
          });
        }

        if (executionOverlay.layers.vehicles && executionTrip.vehiclePosition) {
          const vehicleEl = document.createElement('div');
          vehicleEl.className = 'execution-vehicle-marker';
          vehicleEl.style.width = '30px';
          vehicleEl.style.height = '30px';
          vehicleEl.style.borderRadius = '9px';
          vehicleEl.style.background = executionTrip.color;
          vehicleEl.style.border = '2px solid #fff';
          vehicleEl.style.display = 'grid';
          vehicleEl.style.placeItems = 'center';
          vehicleEl.style.boxShadow = '0 3px 12px rgba(15,23,42,0.35)';
          vehicleEl.style.opacity = markerOpacity;
          vehicleEl.style.cursor = 'pointer';
          vehicleEl.style.fontSize = '15px';
          vehicleEl.textContent = '🚚';
          vehicleEl.title = `${executionTrip.id} current vehicle position`;
          vehicleEl.addEventListener('mouseenter', () => onExecutionActivityHover?.(executionTrip.id, null));
          vehicleEl.addEventListener('mouseleave', () => onExecutionActivityHover?.(null, null));
          vehicleEl.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            onExecutionTripClick?.(executionTrip.id);
          });
          const vehicleMarker = new mapboxgl.Marker({ element: vehicleEl, anchor: 'center' })
            .setLngLat(executionTrip.vehiclePosition)
            .addTo(map.current);
          markersRef.current.push(vehicleMarker);
        }
        tripTouchPointMarkersRef.current[trip.name] = [];
      } else if (!(editMode && editSourceTrip === trip.name)) {
        addBaseTouchPointMarkersForTrip(trip);
      } else {
        tripTouchPointMarkersRef.current[trip.name] = [];
      }
      
      // Add delivery sequence lines if showSequence is true
      if (allowSequenceLines && showSequence && trip.tpDetails.length > 1) {
        // Create a GeoJSON line string
        const lineCoords = sortedPoints
          .map(tp => normalizeLngLat(tp.lngLat))
          .filter((coords): coords is [number, number] => !!coords);
        if (lineCoords.length < 2) {
          return;
        }
        
        // Add source and layer
        if (map.current) {
            map.current.addSource(`${tripId}-sequence`, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: lineCoords
                }
              }
            });
          
            map.current.addLayer({
              id: `${tripId}-sequence-line`,
              type: 'line',
              source: `${tripId}-sequence`,
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': color,
                'line-width': 2,
                'line-dasharray': [2, 1]
              }
            });

            // Add directional arrows along the line
            map.current.addLayer({
              id: `${tripId}-sequence-arrows`,
              type: 'symbol',
              source: `${tripId}-sequence`,
              layout: {
                'symbol-placement': 'line',
                'symbol-spacing': 50,
                'icon-image': 'triangle-11',
                'icon-size': 0.8,
                'icon-allow-overlap': true
              },
              paint: {
                'icon-color': color
              }
            });
          }
      }
    });
    
    // After trips and touch points, add equipment markers if provided
    if (Array.isArray(equipmentMarkers) && equipmentMarkers.length > 0) {
      equipmentMarkers.forEach((eq) => {
        // Create a custom marker element for equipment
        const el = document.createElement('div');
        el.className = 'equipment-marker';
        el.style.width = '24px';
        el.style.height = '24px';
        el.style.borderRadius = '50%';
        el.style.background = '#0d47a1';
        el.style.border = '2px solid white';
        el.style.boxShadow = '0 0 5px rgba(0,0,0,0.3)';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.color = 'white';
        el.style.fontSize = '14px';
        el.style.cursor = 'pointer';
        el.textContent = '🚛';

        const coords = normalizeLngLat(eq.lngLat);
        if (!coords) {
          return;
        }
        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat(coords)
          .setPopup(new mapboxgl.Popup({ offset: 12 }).setText(eq.type ? `${eq.type} (${eq.id})` : eq.id))
          .addTo(map.current!);

        markersRef.current.push(marker);
      });
    }

    const depotLngLat = normalizeLngLat(taskInfo.config?.sourceLngLat);
    if (map.current && depotLngLat) {
      const depotEl = document.createElement('div');
      depotEl.style.width = '32px';
      depotEl.style.height = '32px';
      depotEl.style.borderRadius = '8px';
      depotEl.style.display = 'flex';
      depotEl.style.alignItems = 'center';
      depotEl.style.justifyContent = 'center';
      depotEl.style.background = mode === 'dark' ? '#1f2937' : '#ffffff';
      depotEl.style.border = mode === 'dark' ? '1.5px solid #64748b' : '1.5px solid #94a3b8';
      depotEl.style.boxShadow = mode === 'dark'
        ? '0 2px 10px rgba(0,0,0,0.45)'
        : '0 2px 10px rgba(15,23,42,0.20)';
      depotEl.style.cursor = 'pointer';
      depotEl.style.color = mode === 'dark' ? '#f8fafc' : '#0f172a';
      depotEl.style.fontSize = '17px';
      depotEl.style.fontWeight = '700';
      depotEl.textContent = '🏬';

      const marker = new mapboxgl.Marker({ element: depotEl })
        .setLngLat(depotLngLat)
        .setPopup(
          new mapboxgl.Popup({ offset: 12 }).setText(
            taskInfo.config?.id
              ? `Depot ${taskInfo.config.id} (${depotLngLat[1].toFixed(5)}, ${depotLngLat[0].toFixed(5)})`
              : `Depot (${depotLngLat[1].toFixed(5)}, ${depotLngLat[0].toFixed(5)})`
          )
        )
        .addTo(map.current);
      markersRef.current.push(marker);
    }

    if (map.current && geoCorrectionTouchPoint && geoCorrectionDraftLngLat) {
      const draftEl = document.createElement('div');
      draftEl.style.width = '28px';
      draftEl.style.height = '28px';
      draftEl.style.borderRadius = '50%';
      draftEl.style.background = '#dc2626';
      draftEl.style.border = '3px solid #fff7ed';
      draftEl.style.boxShadow = '0 0 0 5px rgba(220,38,38,0.20), 0 0 16px rgba(220,38,38,0.72)';
      draftEl.style.display = 'flex';
      draftEl.style.alignItems = 'center';
      draftEl.style.justifyContent = 'center';
      draftEl.style.color = '#fff';
      draftEl.style.fontSize = '11px';
      draftEl.style.fontWeight = '900';
      draftEl.style.cursor = 'crosshair';
      draftEl.textContent = 'N';

      const draftMarker = new mapboxgl.Marker({ element: draftEl, anchor: 'center' })
        .setLngLat(geoCorrectionDraftLngLat)
        .addTo(map.current);
      markersRef.current.push(draftMarker);
    }

  };

  // Function to get color based on touch point accuracy
  const getAccuracyColor = (touchPoint: TouchPoint) => {
    // Keep default coloring if present
    if (touchPoint.accuracyLevel) return COLORS.touchPointAccuracy[touchPoint.accuracyLevel];
    return COLORS.touchPointAccuracy['HIGH'];
  };

  const syncEditOverlay = useCallback(() => {
    if (!map.current || !mapLoaded || !taskInfo) {
      return;
    }

    const previousTripName = previousEditSourceTripRef.current;
    if (previousTripName && previousTripName !== editSourceTrip) {
      const previousTrip = taskInfo.trips.find((trip) => trip.name === previousTripName);
      if (previousTrip && visibleTrips.includes(previousTrip.name)) {
        removeBaseTouchPointMarkersForTrip(previousTripName);
        addBaseTouchPointMarkersForTrip(previousTrip);
      }
    }

    if (!editMode || !editSourceTrip) {
      clearEditOverlay();
      previousEditSourceTripRef.current = null;
      return;
    }

    const sourceTrip = taskInfo.trips.find((trip) => trip.name === editSourceTrip);
    if (!sourceTrip || !visibleTrips.includes(sourceTrip.name)) {
      clearEditOverlay();
      previousEditSourceTripRef.current = editSourceTrip;
      return;
    }

    removeBaseTouchPointMarkersForTrip(sourceTrip.name);

    const pointFeatures = sourceTrip.tpDetails
      .map((touchPoint) => {
        const lngLat = normalizeLngLat(touchPoint.lngLat);
        if (!lngLat) {
          return null;
        }

        return {
          type: 'Feature',
          properties: {
            tpId: touchPoint.tpId,
            label: getTouchPointEditLabel(touchPoint),
            selected: selectedTouchPointIds.has(touchPoint.tpId),
            slotBreach: touchPoint.timeBreachMin > 0,
          },
          geometry: {
            type: 'Point',
            coordinates: lngLat,
          },
        };
      })
      .filter((feature): feature is any => feature != null);

    const pointData = {
      type: 'FeatureCollection',
      features: pointFeatures,
    };

    const polygonData = {
      type: 'FeatureCollection',
      features: sourceTrip.geoMarkers.tripBoundaryLngLats.length > 2
        ? [{
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [sourceTrip.geoMarkers.tripBoundaryLngLats],
            },
          }]
        : [],
    };

    const existingPointSource = map.current.getSource(EDIT_POINT_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (existingPointSource) {
      existingPointSource.setData(pointData as any);
    } else {
      map.current.addSource(EDIT_POINT_SOURCE_ID, {
        type: 'geojson',
        data: pointData as any,
      });
    }

    const existingPolygonSource = map.current.getSource(EDIT_POLYGON_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (existingPolygonSource) {
      existingPolygonSource.setData(polygonData as any);
    } else {
      map.current.addSource(EDIT_POLYGON_SOURCE_ID, {
        type: 'geojson',
        data: polygonData as any,
      });
    }

    if (!map.current.getLayer(EDIT_POLYGON_FILL_LAYER_ID)) {
      map.current.addLayer({
        id: EDIT_POLYGON_FILL_LAYER_ID,
        type: 'fill',
        source: EDIT_POLYGON_SOURCE_ID,
        paint: {
          'fill-color': EDIT_HIGHLIGHT_COLOR,
          'fill-opacity': 0.18,
        },
      });
    }

    if (!map.current.getLayer(EDIT_POLYGON_LINE_LAYER_ID)) {
      map.current.addLayer({
        id: EDIT_POLYGON_LINE_LAYER_ID,
        type: 'line',
        source: EDIT_POLYGON_SOURCE_ID,
        paint: {
          'line-color': EDIT_HIGHLIGHT_COLOR,
          'line-width': 3.5,
          'line-opacity': 0.98,
          'line-dasharray': EDIT_OUTLINE_DASH_SEQUENCE[0],
        },
      });
    }

    if (!map.current.getLayer(EDIT_POINT_CIRCLE_LAYER_ID)) {
      map.current.addLayer({
        id: EDIT_POINT_CIRCLE_LAYER_ID,
        type: 'circle',
        source: EDIT_POINT_SOURCE_ID,
        paint: {
          'circle-radius': 13,
          'circle-color': [
            'case',
            ['boolean', ['get', 'slotBreach'], false],
            '#fee2e2',
            '#ffffff',
          ],
          'circle-stroke-color': EDIT_HIGHLIGHT_COLOR,
          'circle-stroke-width': 2.5,
        },
      });
    }

    if (!map.current.getLayer(EDIT_POINT_HIT_LAYER_ID)) {
      map.current.addLayer({
        id: EDIT_POINT_HIT_LAYER_ID,
        type: 'circle',
        source: EDIT_POINT_SOURCE_ID,
        paint: {
          'circle-radius': 20,
          'circle-color': '#ffffff',
          'circle-opacity': 0.01,
        },
      });
    }

    if (!map.current.getLayer(EDIT_POINT_SELECTED_RING_LAYER_ID)) {
      map.current.addLayer({
        id: EDIT_POINT_SELECTED_RING_LAYER_ID,
        type: 'circle',
        source: EDIT_POINT_SOURCE_ID,
        filter: ['==', ['get', 'selected'], true],
        paint: {
          'circle-radius': 15.5,
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-color': '#16a34a',
          'circle-stroke-width': 2,
          'circle-stroke-opacity': 0.9,
        },
      });
    }

    if (!map.current.getLayer(EDIT_POINT_LABEL_LAYER_ID)) {
      map.current.addLayer({
        id: EDIT_POINT_LABEL_LAYER_ID,
        type: 'symbol',
        source: EDIT_POINT_SOURCE_ID,
        layout: {
          'text-field': ['to-string', ['get', 'label']],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': [
            'case',
            ['<=', ['length', ['to-string', ['get', 'label']]], 3], 10,
            ['<=', ['length', ['to-string', ['get', 'label']]], 5], 8.6,
            ['<=', ['length', ['to-string', ['get', 'label']]], 6], 7.6,
            ['<=', ['length', ['to-string', ['get', 'label']]], 8], 6.8,
            6.1,
          ],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': [
            'case',
            ['boolean', ['get', 'slotBreach'], false],
            '#7f1d1d',
            '#0f172a',
          ],
        },
      });
    }

    if (!map.current.getLayer(EDIT_POINT_SELECTED_BADGE_LAYER_ID)) {
      map.current.addLayer({
        id: EDIT_POINT_SELECTED_BADGE_LAYER_ID,
        type: 'circle',
        source: EDIT_POINT_SOURCE_ID,
        filter: ['==', ['get', 'selected'], true],
        paint: {
          'circle-radius': 5,
          'circle-color': '#16a34a',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.25,
          'circle-translate': [0, -10],
        },
      });
    }

    if (!map.current.getLayer(EDIT_POINT_SELECTED_TICK_LAYER_ID)) {
      map.current.addLayer({
        id: EDIT_POINT_SELECTED_TICK_LAYER_ID,
        type: 'symbol',
        source: EDIT_POINT_SOURCE_ID,
        filter: ['==', ['get', 'selected'], true],
        layout: {
          'text-field': '✓',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 7,
          'text-offset': [0, -1.42],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });
    }

    animatedEditOutlineLayerIdsRef.current = polygonData.features.length > 0
      ? [EDIT_POLYGON_LINE_LAYER_ID]
      : [];
    previousEditSourceTripRef.current = sourceTrip.name;
  }, [
    addBaseTouchPointMarkersForTrip,
    clearEditOverlay,
    editMode,
    editSourceTrip,
    getTouchPointEditLabel,
    mapLoaded,
    removeBaseTouchPointMarkersForTrip,
    selectedTouchPointIds,
    taskInfo,
    visibleTrips,
  ]);

  useEffect(() => {
    syncEditOverlayRef.current = syncEditOverlay;
  }, [syncEditOverlay]);

  useEffect(() => {
    syncInspectOverlayRef.current = syncInspectMapOverlay;
  }, [syncInspectMapOverlay]);

  useEffect(() => {
    syncInspectMapOverlay();
  }, [syncInspectMapOverlay]);

  // Update map when task info or visible trips change
  useEffect(() => {
    if (!map.current || !mapLoaded || !taskInfo) return;
    
    // Clear previous trip layers and markers (do not touch route layers here)
    if (map.current) {
      // Remove existing trip layers and sources
      Array.from(new Set([
        ...taskInfo.trips.map((trip) => trip.name),
        ...executionRenderedTripIdsRef.current,
      ])).forEach((tripName) => {
        const tripId = `trip-${tripName}`;
        
        if (map.current!.getLayer(`${tripId}-fill`)) {
          map.current!.removeLayer(`${tripId}-fill`);
        }
        
        if (map.current!.getLayer(`${tripId}-line`)) {
          map.current!.removeLayer(`${tripId}-line`);
        }
        
        if (map.current!.getLayer(`${tripId}-sequence-line`)) {
          map.current!.removeLayer(`${tripId}-sequence-line`);
        }

        if (map.current!.getLayer(`${tripId}-sequence-arrows`)) {
          map.current!.removeLayer(`${tripId}-sequence-arrows`);
        }

        if (map.current!.getLayer(`${tripId}-route-line`)) {
          map.current!.removeLayer(`${tripId}-route-line`);
        }
        if (map.current!.getLayer(`${tripId}-route-exact-line`)) {
          map.current!.removeLayer(`${tripId}-route-exact-line`);
        }
        if (map.current!.getLayer(`${tripId}-route-fallback-line`)) {
          map.current!.removeLayer(`${tripId}-route-fallback-line`);
        }
        if (map.current!.getLayer(`${tripId}-execution-planned-line`)) {
          map.current!.removeLayer(`${tripId}-execution-planned-line`);
        }
        if (map.current!.getLayer(`${tripId}-execution-actual-line`)) {
          map.current!.removeLayer(`${tripId}-execution-actual-line`);
        }
        
        if (map.current!.getSource(tripId)) {
          map.current!.removeSource(tripId);
        }
        
        if (map.current!.getSource(`${tripId}-sequence`)) {
          map.current!.removeSource(`${tripId}-sequence`);
        }

        if (map.current!.getSource(`${tripId}-route`)) {
          map.current!.removeSource(`${tripId}-route`);
        }
        if (map.current!.getSource(`${tripId}-route-exact`)) {
          map.current!.removeSource(`${tripId}-route-exact`);
        }
        if (map.current!.getSource(`${tripId}-route-fallback`)) {
          map.current!.removeSource(`${tripId}-route-fallback`);
        }
        if (map.current!.getSource(`${tripId}-execution-planned`)) {
          map.current!.removeSource(`${tripId}-execution-planned`);
        }
        if (map.current!.getSource(`${tripId}-execution-actual`)) {
          map.current!.removeSource(`${tripId}-execution-actual`);
        }
      });
      
      // Remove non-touchpoint markers
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      executionMarkerElementsRef.current = {};

      // Remove base trip touchpoint markers
      Object.values(tripTouchPointMarkersRef.current).forEach((tripMarkers) => {
        tripMarkers.forEach((marker) => marker.remove());
      });
      tripTouchPointMarkersRef.current = {};

      // Reset edit overlays before rebuilding stable base content
      clearEditOverlay();
    }
    
    // Rebuild layers using latest visibility toggles
    addTripLayers();
    executionRenderedTripIdsRef.current = executionOverlay?.trips.map((trip) => trip.id) ?? [];

    // Preserve current zoom/pan on touch-point selection updates.
    const autoFitSignature = JSON.stringify({
      taskId: taskInfo.taskId,
      visibleTrips: [...visibleTrips].sort(),
      showPolygons,
      showSequence,
      allowRouteLines,
      allowSequenceLines,
      showRoutes,
    });
    const shouldAutoFit = lastAutoFitSignatureRef.current !== autoFitSignature;
    lastAutoFitSignatureRef.current = autoFitSignature;

    if (shouldAutoFit) {
      // Fit bounds only when viewport-affecting toggles/data changed.
      fitMapToVisible();
    }
    // Layer rebuilds intentionally track viewport toggles instead of helper identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    taskInfo,
    visibleTrips,
    showPolygons,
    showSequence,
    allowRouteLines,
    allowSequenceLines,
    showRoutes,
    routeGeometryByTrip,
    executionOverlay,
    mapLoaded,
    geoCorrectionTouchPoint,
    geoCorrectionDraftLngLat,
    clearEditOverlay,
  ]);

  useEffect(() => {
    syncEditOverlay();
  }, [syncEditOverlay]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !editMode || !editSourceTrip) {
      return;
    }

    const handlePointerEnter = () => {
      if (map.current) {
        map.current.getCanvas().style.cursor = 'pointer';
      }
    };

    const handlePointerLeave = () => {
      if (map.current) {
        map.current.getCanvas().style.cursor = '';
      }
      scheduleTouchPointCardHide();
    };

    const handlePointerMove = (event: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
      const feature = event.features?.[0];
      const tpId = typeof feature?.properties?.tpId === 'string' ? feature.properties.tpId : null;
      if (!tpId) {
        return;
      }
      const touchPoint = editTouchPointById.get(tpId);
      if (!touchPoint) {
        return;
      }
      clearTouchPointCardHideTimer();
      setHoveredTouchPoint(touchPoint);
      const originalEvent = event.originalEvent as MouseEvent;
      setTpInfoPosition({ x: originalEvent.clientX, y: originalEvent.clientY });
    };

    const handlePointClick = (event: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
      const feature = event.features?.[0];
      const tpId = typeof feature?.properties?.tpId === 'string' ? feature.properties.tpId : null;
      if (!tpId) {
        return;
      }
      const touchPoint = editTouchPointById.get(tpId);
      if (!touchPoint) {
        return;
      }
      event.preventDefault();
      event.originalEvent.stopPropagation();
      clearFocusTargetMarker();
      clearTouchPointCardHideTimer();
      setHoveredTouchPoint(touchPoint);
      const originalEvent = event.originalEvent as MouseEvent;
      setTpInfoPosition({ x: originalEvent.clientX, y: originalEvent.clientY });
      onTouchPointSelect(touchPoint);
    };

    const layerId = EDIT_POINT_HIT_LAYER_ID;
    if (map.current?.getLayer(layerId)) {
      map.current.on('mouseenter', layerId, handlePointerEnter);
      map.current.on('mouseleave', layerId, handlePointerLeave);
      map.current.on('mousemove', layerId, handlePointerMove);
      map.current.on('click', layerId, handlePointClick);
    }

    return () => {
      if (map.current?.getLayer(layerId)) {
        map.current.off('mouseenter', layerId, handlePointerEnter);
        map.current.off('mouseleave', layerId, handlePointerLeave);
        map.current.off('mousemove', layerId, handlePointerMove);
        map.current.off('click', layerId, handlePointClick);
      }
      if (map.current) {
        map.current.getCanvas().style.cursor = '';
      }
    };
  }, [
    clearTouchPointCardHideTimer,
    clearFocusTargetMarker,
    editMode,
    editSourceTrip,
    editTouchPointById,
    mapLoaded,
    onTouchPointSelect,
    scheduleTouchPointCardHide,
  ]);

  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return;
    }

    const animatedLayerIds = animatedEditOutlineLayerIdsRef.current.filter((layerId) => map.current?.getLayer(layerId));
    if (!animatedLayerIds.length) {
      return;
    }

    let sequenceIndex = 0;
    const updateDashPattern = () => {
      if (!map.current) {
        return;
      }
      const dashArray = EDIT_OUTLINE_DASH_SEQUENCE[sequenceIndex];
      animatedLayerIds.forEach((layerId) => {
        if (map.current?.getLayer(layerId)) {
          map.current.setPaintProperty(layerId, 'line-dasharray', dashArray);
        }
      });
      sequenceIndex = (sequenceIndex + 1) % EDIT_OUTLINE_DASH_SEQUENCE.length;
    };

    updateDashPattern();
    const intervalId = window.setInterval(updateDashPattern, 120);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [editMode, editSourceTrip, mapLoaded, mode, showPolygons, taskInfo, visibleTrips]);

  useEffect(() => {
    return () => {
      clearTripCardHideTimer();
      clearTouchPointCardHideTimer();
    };
  }, [clearTouchPointCardHideTimer]);

  useEffect(() => {
    focusTargetMarkerRef.current?.remove();
    focusTargetMarkerRef.current = null;

    if (!map.current || !mapLoaded || !focusTargetMarker) {
      return undefined;
    }

    const marker = new mapboxgl.Marker({
      element: createFocusTargetMarkerElement(focusTargetMarker.kind),
      anchor: 'center',
    })
      .setLngLat(focusTargetMarker.lngLat)
      .addTo(map.current);

    focusTargetMarkerRef.current = marker;

    return () => {
      marker.remove();
      if (focusTargetMarkerRef.current === marker) {
        focusTargetMarkerRef.current = null;
      }
    };
  }, [focusTargetMarker, mapLoaded]);

  useEffect(() => {
    if (!focusLngLat && !focusTouchPointRequest && !focusTripRequest) {
      clearFocusTargetMarker();
    }
  }, [clearFocusTargetMarker, focusLngLat, focusTouchPointRequest, focusTripRequest]);

  useEffect(() => {
    if (!focusTargetMarker || typeof document === 'undefined') {
      return undefined;
    }

    const handlePointerDown = () => {
      clearFocusTargetMarker();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [clearFocusTargetMarker, focusTargetMarker]);

  // Center map when point focus changes, using the currently unobstructed map region.
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const pointFocusLngLat = focusTouchPointRequest?.lngLat ?? focusLngLat;
    if (pointFocusLngLat && Array.isArray(pointFocusLngLat)) {
      setFocusTargetMarker({
        kind: 'touch-point',
        lngLat: pointFocusLngLat,
        requestId: focusTouchPointRequest?.requestId ?? 0,
      });
      fitMapToCoords([pointFocusLngLat], {
        singlePointZoom: 14,
        padding: getFocusPadding(),
      });
    }
    // Focus should move only for an explicit focus request, not every overlay drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusLngLat, focusTouchPointRequest, mapLoaded]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !taskInfo || !focusTripRequest) {
      return;
    }
    if (!visibleTrips.includes(focusTripRequest.tripName)) {
      return;
    }
    const trip = taskInfo.trips.find((candidate) => candidate.name === focusTripRequest.tripName);
    const tripCenter = normalizeLngLat(trip?.geoMarkers?.tripCenterLngLat);
    if (tripCenter) {
      setFocusTargetMarker({
        kind: 'trip-vehicle',
        lngLat: tripCenter,
        requestId: focusTripRequest.requestId,
      });
    } else {
      clearFocusTargetMarker();
    }
    fitMapToTrip(focusTripRequest.tripName, { focusAware: true });
    // Trip focus is driven by explicit request ids rather than helper identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTripRequest, mapLoaded, taskInfo, visibleTrips]);

  useEffect(() => {
    if (!map.current || !mapLoaded || geoCorrectionTouchPoint) return;
    const handleMapClick = () => {
      clearFocusTargetMarker();
      setHoveredTouchPoint(null);
      setTpInfoPosition(null);
      setExternalTpInfo(null);
      onClearExternalTouchPoint?.();
    };
    map.current.on('click', handleMapClick);
    return () => {
      map.current?.off('click', handleMapClick);
    };
  }, [clearFocusTargetMarker, geoCorrectionTouchPoint, mapLoaded, onClearExternalTouchPoint]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !geoCorrectionTouchPoint || !onGeoCorrectionDraft) {
      return;
    }
    const handleMapClick = (event: mapboxgl.MapMouseEvent) => {
      onGeoCorrectionDraft([event.lngLat.lng, event.lngLat.lat]);
    };
    map.current.on('click', handleMapClick);
    return () => {
      map.current?.off('click', handleMapClick);
    };
  }, [geoCorrectionTouchPoint, mapLoaded, onGeoCorrectionDraft]);

  // Center map on source location
  const centerMapOnSource = () => {
    if (!map.current || !taskInfo) return;
    
    const sourceLocation = taskInfo.config.sourceLngLat;
    map.current.flyTo({
      center: sourceLocation,
      zoom: 12
    });
  };

  const resolvedEditCardPosition = editCardPosition ?? (
    moveCardUsesAnchoredPosition
      ? resolveEditCardAnchoredPosition(editCardSize)
      : getEditCardFallbackPosition(editCardSize)
  );
  const inspectGeometry = inspectDraftGeometry;
  const inspectCapsule = inspectGeometry?.kind === 'capsule' ? inspectGeometry : null;
  const inspectRectangle = inspectGeometry?.kind === 'rectangle' ? inspectGeometry : null;
  const inspectCapsuleDeltaX = inspectCapsule ? inspectCapsule.end.x - inspectCapsule.start.x : 0;
  const inspectCapsuleDeltaY = inspectCapsule ? inspectCapsule.end.y - inspectCapsule.start.y : 0;
  const inspectCapsuleSegmentLength = inspectCapsule
    ? Math.hypot(inspectCapsuleDeltaX, inspectCapsuleDeltaY)
    : 0;
  const inspectCapsuleAngleDeg = inspectCapsule
    ? (Math.atan2(inspectCapsuleDeltaY, inspectCapsuleDeltaX) * 180) / Math.PI
    : 0;
  const inspectRectangleBounds = inspectRectangle ? getInspectRectangleBounds(inspectRectangle) : null;
  const totalSelectedPoints = activeSelectionSummary?.touchPoints.length ?? 0;
  const showDestinationChooser = moveCardPhase === 'destinations' || moveCardPhase === 'new-trip';
  const destinationChooserTitle = moveCardPhase === 'new-trip' ? 'Create New Trip' : 'Choose Destination';
  const hasSingleSelectionTrip = selectionTripSummaries.length === 1;

  const getTonePalette = (tone: DenseTone = 'neutral') => {
    switch (tone) {
      case 'info':
        return { base: '#2563eb', text: '#1d4ed8' };
      case 'good':
        return { base: '#0f766e', text: '#0f766e' };
      case 'warn':
        return { base: '#b45309', text: '#b45309' };
      case 'danger':
        return { base: '#b42318', text: '#b42318' };
      case 'accent':
        return { base: '#4f46e5', text: '#4338ca' };
      default:
        return { base: '#64748b', text: mode === 'dark' ? '#cbd5e1' : '#334155' };
    }
  };

  const renderSummaryMetric = (
    value: string,
    unit: string | undefined,
    options: {
      icon: DenseIconKey;
      tone?: DenseTone;
      accent?: boolean;
      compact?: boolean;
    }
  ) => {
    const palette = getTonePalette(options.accent ? 'info' : options.tone);
    const compact = options.compact ?? false;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: compact ? 0.42 : 0.48,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: compact ? 17 : 19,
            height: compact ? 17 : 19,
            borderRadius: 1.1,
            border: '1px solid',
            borderColor: alpha(palette.base, 0.15),
            bgcolor: alpha(palette.base, 0.06),
            color: palette.text,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {renderDenseIcon(options.icon, {
            sx: {
              fontSize: compact ? 10.5 : 11.5,
              color: 'inherit',
            },
          })}
        </Box>
        <Typography
          sx={{
            fontSize: compact ? 11.2 : 12.2,
            fontWeight: 500,
            lineHeight: 1.05,
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {value}
          {unit ? (
            <Box
              component="span"
              sx={{
                ml: 0.22,
                fontSize: compact ? 9.4 : 10,
                fontWeight: 400,
                color: 'text.secondary',
              }}
            >
              {unit}
            </Box>
          ) : null}
        </Typography>
      </Box>
    );
  };

  const getWeightMetricParts = (weightKg: number): { value: string; unit: string } => (
    weightKg >= 1000
      ? { value: (weightKg / 1000).toFixed(0), unit: 'T' }
      : { value: Math.round(weightKg).toString(), unit: 'kg' }
  );

  const getVolumeMetricParts = (volumeCuFt: number): { value: string; unit: string } => ({
    value: Math.round(volumeCuFt).toString(),
    unit: 'cu ft',
  });

  const renderCapacityChip = (
    key: DestinationCapacityKey,
    shortLabel: string,
    value: number | null,
    warnings: DestinationWarningSet
  ) => {
    const warning = warnings.has(key);
    const palette = getTonePalette(warning ? 'warn' : 'good');
    const iconByKey: Record<DestinationCapacityKey, DenseIconKey> = {
      weightKg: 'weight',
      touchPoints: 'touchPoint',
      volumeCuFt: 'volume',
      distanceKm: 'distance',
      timeMinutes: 'time',
    };
    return (
      <Box
        key={key}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.45,
          px: 0.72,
          py: 0.35,
          borderRadius: 999,
          border: '1px solid',
          borderColor: alpha(palette.base, warning ? 0.28 : 0.18),
          bgcolor: alpha(palette.base, mode === 'dark' ? (warning ? 0.18 : 0.12) : (warning ? 0.1 : 0.06)),
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: palette.text,
            bgcolor: alpha(palette.base, mode === 'dark' ? 0.22 : 0.1),
            flexShrink: 0,
          }}
        >
          {renderDenseIcon(iconByKey[key], { sx: { fontSize: 10, color: 'inherit' } })}
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 1, color: palette.text, fontSize: 10.5 }}>
          {formatCapacityValue(key, value)}
        </Typography>
        {warning ? (
          <Tooltip title={`Requested move exceeds available ${shortLabel.toLowerCase()}`}>
            <WarningAmberRoundedIcon sx={{ fontSize: 13, color: '#f59e0b' }} />
          </Tooltip>
        ) : null}
      </Box>
    );
  };

  const renderSourceTripPill = (tripName: string) => (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: 0.36,
        borderRadius: 999,
        border: '1px solid',
        borderColor: alpha('#64748b', 0.18),
        bgcolor: alpha('#64748b', mode === 'dark' ? 0.18 : 0.06),
        minWidth: 0,
      }}
    >
      {renderDenseIcon('vehicle', { sx: { fontSize: 11, color: 'text.secondary' } })}
      <Typography variant="caption" sx={{ lineHeight: 1, color: 'text.secondary', fontSize: 10.5 }}>
        {tripName}
      </Typography>
    </Box>
  );

  const formatCapacityValue = (key: DestinationCapacityKey, value: number | null): string => {
    if (value == null) {
      return '--';
    }
    switch (key) {
      case 'weightKg':
        return formatWeight(value);
      case 'touchPoints':
        return `${Math.round(value)} tp`;
      case 'volumeCuFt':
        return formatVolume(value);
      case 'distanceKm':
        return formatDistance(value);
      case 'timeMinutes':
        return formatDurationFromMinutes(value);
      default:
        return String(Math.round(value));
    }
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      zIndex: 1
    }}>
      <div 
        ref={mapContainer} 
        style={{ 
          width: '100%', 
          height: '100%', 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0 
        }} 
      />
      
      {/* Legend overlay */}
      {showMapLegend && (
      <Paper
        elevation={2}
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          p: 1,
          px: 1.5,
          borderRadius: 1,
          backgroundColor: mode === 'dark' ? 'rgba(38,38,38,0.9)' : 'rgba(255,255,255,0.9)',
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <Stack spacing={0.8}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            {[
              { label: 'Origin', color: '#1e88e5', border: '#0d47a1' },
              { label: 'Pickup', color: '#2e7d32', border: '#ffffff' },
              { label: 'Drop', color: '#ef6c00', border: '#ffffff' },
              { label: 'Unassigned', color: '#6a1b9a', border: '#ffffff' },
            ].map((item) => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.color, border: `2px solid ${item.border}` }} />
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
              </Box>
            ))}
            {hasGeoQualityTouchPoints && (
              <Button
                size="small"
                variant={geoLegendExpanded ? 'contained' : 'outlined'}
                onClick={() => setGeoLegendExpanded((prev) => !prev)}
                sx={{ minWidth: 0, px: 1, py: 0.2, borderRadius: 999, fontSize: 11, lineHeight: 1.2 }}
              >
                Geo
              </Button>
            )}
          </Stack>
          {hasGeoQualityTouchPoints && geoLegendExpanded && (
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
              {[
                { label: 'Accurate', color: '#16a34a' },
                { label: 'Low confidence', color: '#DE3163' },
                { label: 'Derived', color: '#e5ac62' },
                { label: 'Corrected', color: '#7c3aed' },
              ].map((item) => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.color, border: '1px solid rgba(15,23,42,0.12)' }} />
                  <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
      )}
      
      {(showMapOptions || inspectAvailable || routeGeometryRefreshAvailable) && (
        <Stack
          direction="row"
          spacing={TOP_TOOLBAR_GAP_PX / 8}
          sx={{
            position: 'absolute',
            top: TOP_TOOLBAR_TOP_PX,
            right: TOP_TOOLBAR_RIGHT_PX,
            zIndex: 4,
          }}
        >
          {inspectAvailable && (
            <React.Fragment>
              <Tooltip title={inspectMode && inspectTool === 'capsule' ? 'Exit pill inspect' : 'Inspect with pill'}>
                <span>
                  <IconButton
                    aria-label={inspectMode && inspectTool === 'capsule' ? 'Exit pill inspect' : 'Inspect with pill'}
                    onClick={() => handleInspectToolSelect('capsule')}
                    disabled={editMode || Boolean(geoCorrectionTouchPoint)}
                    sx={{
                      width: TOP_TOOLBAR_BUTTON_SIZE_PX,
                      height: TOP_TOOLBAR_BUTTON_SIZE_PX,
                      backgroundColor: inspectMode && inspectTool === 'capsule'
                        ? (mode === 'dark' ? 'rgba(37,99,235,0.92)' : 'rgba(219,234,254,0.96)')
                        : (mode === 'dark' ? 'rgba(66, 66, 66, 0.9)' : 'rgba(255, 255, 255, 0.9)'),
                      color: inspectMode && inspectTool === 'capsule'
                        ? (mode === 'dark' ? '#eff6ff' : INSPECT_CENTER_COLOR)
                        : 'inherit',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      '&:hover': {
                        backgroundColor: inspectMode && inspectTool === 'capsule'
                          ? (mode === 'dark' ? 'rgba(29,78,216,0.96)' : 'rgba(191,219,254,0.98)')
                          : (mode === 'dark' ? 'rgba(80, 80, 80, 0.9)' : 'rgba(240, 240, 240, 0.9)'),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 8,
                        borderRadius: 999,
                        border: '2px solid currentColor',
                        bgcolor: 'transparent',
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={inspectMode && inspectTool === 'rectangle' ? 'Exit rectangle inspect' : 'Inspect with rectangle'}>
                <span>
                  <IconButton
                    aria-label={inspectMode && inspectTool === 'rectangle' ? 'Exit rectangle inspect' : 'Inspect with rectangle'}
                    onClick={() => handleInspectToolSelect('rectangle')}
                    disabled={editMode || Boolean(geoCorrectionTouchPoint)}
                    sx={{
                      width: TOP_TOOLBAR_BUTTON_SIZE_PX,
                      height: TOP_TOOLBAR_BUTTON_SIZE_PX,
                      backgroundColor: inspectMode && inspectTool === 'rectangle'
                        ? (mode === 'dark' ? 'rgba(37,99,235,0.92)' : 'rgba(219,234,254,0.96)')
                        : (mode === 'dark' ? 'rgba(66, 66, 66, 0.9)' : 'rgba(255, 255, 255, 0.9)'),
                      color: inspectMode && inspectTool === 'rectangle'
                        ? (mode === 'dark' ? '#eff6ff' : INSPECT_CENTER_COLOR)
                        : 'inherit',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      '&:hover': {
                        backgroundColor: inspectMode && inspectTool === 'rectangle'
                          ? (mode === 'dark' ? 'rgba(29,78,216,0.96)' : 'rgba(191,219,254,0.98)')
                          : (mode === 'dark' ? 'rgba(80, 80, 80, 0.9)' : 'rgba(240, 240, 240, 0.9)'),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 12,
                        borderRadius: '2px',
                        border: '2px solid currentColor',
                        bgcolor: 'transparent',
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </React.Fragment>
          )}

          {routeGeometryRefreshAvailable && (
            <Tooltip title={routeGeometryRefreshing ? 'Refreshing routes' : 'Refresh route geometry'}>
              <span>
                <IconButton
                  aria-label="Refresh route geometry"
                  onClick={onRefreshRouteGeometry}
                  disabled={routeGeometryRefreshing || !onRefreshRouteGeometry}
                  sx={{
                    width: TOP_TOOLBAR_BUTTON_SIZE_PX,
                    height: TOP_TOOLBAR_BUTTON_SIZE_PX,
                    backgroundColor: mode === 'dark' ? 'rgba(66, 66, 66, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: mode === 'dark' ? 'rgba(80, 80, 80, 0.9)' : 'rgba(240, 240, 240, 0.9)',
                    },
                  }}
                >
                  {routeGeometryRefreshing
                    ? <CircularProgress size={18} />
                    : <RefreshRoundedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {showMapOptions && (
            <IconButton
              aria-label="Map options"
              sx={{
                width: TOP_TOOLBAR_BUTTON_SIZE_PX,
                height: TOP_TOOLBAR_BUTTON_SIZE_PX,
                backgroundColor: mode === 'dark' ? 'rgba(66, 66, 66, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                '&:hover': {
                  backgroundColor: mode === 'dark' ? 'rgba(80, 80, 80, 0.9)' : 'rgba(240, 240, 240, 0.9)',
                },
              }}
              onClick={() => setMapOptionsVisible(!mapOptionsVisible)}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}

      {showMapOptions && mapOptionsVisible && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: FLOATING_PANEL_TOP_PX,
            right: TOP_TOOLBAR_RIGHT_PX,
            p: 1.5,
            zIndex: 4,
            backgroundColor: mode === 'dark' ? 'rgba(66, 66, 66, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={showPolygons}
                  onChange={() => onMapOptionsChange('polygons')}
                  size="small"
                />
              }
              label={<Typography variant="body2">Trip Areas</Typography>}
            />

            {allowSequenceLines && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showSequence}
                    onChange={() => onMapOptionsChange('sequence')}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Delivery Sequence</Typography>}
              />
            )}

            {allowRouteLines && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showRoutes}
                    onChange={() => setShowRoutes((prev) => !prev)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Route Lines</Typography>}
              />
            )}

            <Tooltip title="Center on Source Location">
              <IconButton
                size="small"
                color="primary"
                onClick={centerMapOnSource}
              >
                <MyLocationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      {inspectMode && (
        <Box
          onPointerDown={handleInspectOverlayPointerDown}
          onPointerMove={handleInspectOverlayPointerMove}
          onPointerUp={handleInspectOverlayPointerUp}
          onPointerCancel={handleInspectOverlayPointerCancel}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            cursor: showDestinationChooser ? 'default' : 'crosshair',
            touchAction: 'none',
            pointerEvents: showDestinationChooser || inspectSelection ? 'none' : 'auto',
          }}
        >
          {inspectCapsule && (
            <Box
              sx={{
                position: 'absolute',
                left: inspectCapsule.start.x - inspectCapsule.radius,
                top: inspectCapsule.start.y - inspectCapsule.radius,
                width: (inspectCapsule.radius * 2) + inspectCapsuleSegmentLength,
                height: inspectCapsule.radius * 2,
                borderRadius: 999,
                border: `2px solid ${INSPECT_OUTLINE_COLOR}`,
                bgcolor: INSPECT_FILL_COLOR,
                boxShadow: '0 0 0 1px rgba(255,255,255,0.48) inset',
                pointerEvents: 'none',
                transform: `rotate(${inspectCapsuleAngleDeg}deg)`,
                transformOrigin: `${inspectCapsule.radius}px ${inspectCapsule.radius}px`,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: inspectCapsule.radius,
                  top: '50%',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: INSPECT_CENTER_COLOR,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: '0 0 0 3px rgba(255,255,255,0.72)',
                }}
              />
              {inspectCapsuleSegmentLength > 2 && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: inspectCapsule.radius + inspectCapsuleSegmentLength,
                    top: '50%',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: '#ffffff',
                    border: `2px solid ${INSPECT_OUTLINE_COLOR}`,
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.4)',
                  }}
                />
              )}
            </Box>
          )}
          {inspectRectangle && inspectRectangleBounds && (
            <Box
              sx={{
                position: 'absolute',
                left: `${inspectRectangleBounds.left}px`,
                top: `${inspectRectangleBounds.top}px`,
                width: `${Math.max(1, inspectRectangleBounds.right - inspectRectangleBounds.left)}px`,
                height: `${Math.max(1, inspectRectangleBounds.bottom - inspectRectangleBounds.top)}px`,
                borderRadius: 1,
                border: `2px solid ${INSPECT_OUTLINE_COLOR}`,
                bgcolor: INSPECT_FILL_COLOR,
                boxShadow: '0 0 0 1px rgba(255,255,255,0.48) inset',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: INSPECT_CENTER_COLOR,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: '0 0 0 3px rgba(255,255,255,0.72)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#ffffff',
                  border: `2px solid ${INSPECT_OUTLINE_COLOR}`,
                  transform: 'translate(50%, 50%)',
                  boxShadow: '0 0 0 2px rgba(255,255,255,0.4)',
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {moveCardVisible && activeSelectionSummary && (
        <Paper
          ref={updateEditCardNode}
          elevation={4}
          sx={{
            position: 'absolute',
            left: resolvedEditCardPosition.x,
            top: resolvedEditCardPosition.y,
            p: 0,
            zIndex: 4,
            width: 328,
            maxWidth: 'calc(100% - 24px)',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)',
            boxShadow: mode === 'dark'
              ? '0 12px 28px rgba(0,0,0,0.52)'
              : '0 12px 28px rgba(15,23,42,0.16)',
            userSelect: editCardDragging ? 'none' : 'auto',
          }}
        >
          <Box
            onPointerDown={handleEditCardPointerDown}
            sx={{
              position: 'relative',
              px: 1.25,
              py: 1.05,
              pl: 1.55,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 0.9,
              cursor: editCardDragging ? 'grabbing' : 'grab',
              bgcolor: mode === 'dark' ? 'rgba(15,23,42,0.92)' : 'rgba(255,255,255,0.94)',
              borderBottom: '1px solid',
              borderColor: alpha(mode === 'dark' ? '#e2e8f0' : '#0f172a', 0.08),
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                bgcolor: inspectSelection ? INSPECT_CENTER_COLOR : '#16a34a',
              },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: 1.2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: inspectSelection ? INSPECT_CENTER_COLOR : '#0f766e',
                    bgcolor: alpha(inspectSelection ? INSPECT_CENTER_COLOR : '#0f766e', mode === 'dark' ? 0.22 : 0.08),
                    flexShrink: 0,
                  }}
                >
                  {renderDenseIcon('edit', { sx: { fontSize: 13, color: 'inherit' } })}
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                  {showDestinationChooser
                    ? destinationChooserTitle
                    : (inspectSelection ? 'Move Selection' : `Move ${editSourceTrip ?? 'Points'}`)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.55, ml: 3.7 }}>
                {showDestinationChooser
                  ? activeMoveScope?.label
                  : hasSingleSelectionTrip
                    ? (selectionTripSummaries[0]?.tripName ?? '1 source trip')
                    : `${selectionTripSummaries.length} source trips`}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.95, mt: 0.75, ml: 3.7 }}>
                {renderSummaryMetric(String(totalSelectedPoints), 'pts', { icon: 'touchPoint', accent: true })}
                {renderSummaryMetric(getWeightMetricParts(activeSelectionSummary.totalWeightKg).value, getWeightMetricParts(activeSelectionSummary.totalWeightKg).unit, { icon: 'weight', tone: 'good' })}
                {renderSummaryMetric(getVolumeMetricParts(activeSelectionSummary.totalVolumeCuFt).value, getVolumeMetricParts(activeSelectionSummary.totalVolumeCuFt).unit, { icon: 'volume', tone: 'accent' })}
              </Box>
            </Box>
            <IconButton size="small" onClick={handleDismissMoveCard} sx={{ mt: -0.35, mr: -0.35 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 1.1 }}>
            {showDestinationChooser ? (
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  {moveCardPhase === 'new-trip' ? (
                    <IconButton size="small" onClick={handleBackToDestinationTrips} sx={{ flexShrink: 0 }}>
                      <ArrowBackRoundedIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                  <TextField
                    size="small"
                    value={destinationSearchTerm}
                    onChange={(event) => setDestinationSearchTerm(event.target.value)}
                    placeholder={moveCardPhase === 'new-trip' ? 'Search vehicle' : 'Search trip'}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {moveCardPhase === 'destinations' ? (
                  <Box
                    role="button"
                    onClick={handleOpenNewTripChooser}
                    sx={{
                      px: 0.95,
                      py: 0.72,
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: alpha(INSPECT_OUTLINE_COLOR, 0.34),
                      bgcolor: alpha(INSPECT_OUTLINE_COLOR, mode === 'dark' ? 0.16 : 0.06),
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1.1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: INSPECT_CENTER_COLOR,
                          bgcolor: alpha(INSPECT_CENTER_COLOR, mode === 'dark' ? 0.22 : 0.1),
                          flexShrink: 0,
                        }}
                      >
                        {renderDenseIcon('vehicle', { sx: { fontSize: 12, color: 'inherit' } })}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.1 }}>
                        Create New Trip
                      </Typography>
                    </Box>
                  </Box>
                ) : null}

                <Stack spacing={0.7} sx={{ maxHeight: 'min(46vh, 280px)', overflowY: 'auto', pr: 0.2 }}>
                  {filteredDestinationOptions.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No destinations match this search.
                    </Typography>
                  ) : filteredDestinationOptions.map((option) => {
                    const warnings = getDestinationWarnings(option.capacities, activeMoveScope);
                    return (
                      <Box
                        key={`${option.kind}:${option.id}`}
                        role="button"
                        onClick={() => {
                          if (!option.disabled && !editSubmitting) {
                            void submitMoveScope(option.id);
                          }
                        }}
                        sx={{
                          px: 1,
                          py: 0.9,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: option.disabled
                            ? alpha(mode === 'dark' ? '#e2e8f0' : '#0f172a', 0.08)
                            : alpha(mode === 'dark' ? '#e2e8f0' : '#0f172a', 0.12),
                          bgcolor: option.disabled
                            ? alpha(mode === 'dark' ? '#0f172a' : '#ffffff', mode === 'dark' ? 0.3 : 0.55)
                            : alpha(mode === 'dark' ? '#0f172a' : '#ffffff', mode === 'dark' ? 0.5 : 0.84),
                          opacity: option.disabled ? 0.45 : 1,
                          cursor: option.disabled || editSubmitting ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.8, alignItems: 'center' }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                              <Box
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 1,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: option.kind === 'existing-trip' ? '#0f766e' : INSPECT_CENTER_COLOR,
                                  bgcolor: alpha(option.kind === 'existing-trip' ? '#0f766e' : INSPECT_CENTER_COLOR, mode === 'dark' ? 0.2 : 0.08),
                                  flexShrink: 0,
                                }}
                              >
                                {renderDenseIcon('vehicle', { sx: { fontSize: 10.5, color: 'inherit' } })}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.15 }}>
                                {option.label}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.28, ml: 3.1 }}>
                              {option.subtitle}
                            </Typography>
                          </Box>
                          {option.disabled ? (
                            <Typography variant="caption" color="text.secondary">
                              Source
                            </Typography>
                          ) : null}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                          {renderCapacityChip('weightKg', 'Avl kg', option.capacities.weightKg, warnings)}
                          {renderCapacityChip('touchPoints', 'Avl tp', option.capacities.touchPoints, warnings)}
                          {renderCapacityChip('volumeCuFt', 'Avl vol', option.capacities.volumeCuFt, warnings)}
                          {renderCapacityChip('timeMinutes', 'Avl time', option.capacities.timeMinutes, warnings)}
                          {renderCapacityChip('distanceKm', 'Avl km', option.capacities.distanceKm, warnings)}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>

                {moveCardPhase === 'destinations' ? (
                  <Typography variant="caption" color="text.secondary">
                    Click a vehicle on the map to move instantly into that trip.
                  </Typography>
                ) : null}
              </Stack>
            ) : (
              <Stack spacing={0.85}>
                {selectionTripSummaries.map((tripSummary) => (
                  <Box
                    key={tripSummary.tripName}
                    sx={{
                      px: 1,
                      py: 0.9,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: alpha(mode === 'dark' ? '#e2e8f0' : '#0f172a', 0.1),
                      bgcolor: alpha(mode === 'dark' ? '#0f172a' : '#ffffff', mode === 'dark' ? 0.48 : 0.84),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: hasSingleSelectionTrip ? 'center' : 'flex-start', justifyContent: 'space-between', gap: 0.8 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#0f766e',
                              bgcolor: alpha('#0f766e', mode === 'dark' ? 0.2 : 0.08),
                              flexShrink: 0,
                            }}
                          >
                            {renderDenseIcon('vehicle', { sx: { fontSize: 10.5, color: 'inherit' } })}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.15 }}>
                            {tripSummary.tripName}
                          </Typography>
                        </Box>
                        {hasSingleSelectionTrip ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.6 }}>
                            {renderSourceTripPill('Source trip')}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.7 }}>
                            {renderSummaryMetric(String(tripSummary.pointCount), 'pts', { icon: 'touchPoint', accent: true, compact: true })}
                            {renderSummaryMetric(getWeightMetricParts(tripSummary.weightKg).value, getWeightMetricParts(tripSummary.weightKg).unit, { icon: 'weight', tone: 'good', compact: true })}
                            {renderSummaryMetric(getVolumeMetricParts(tripSummary.volumeCuFt).value, getVolumeMetricParts(tripSummary.volumeCuFt).unit, { icon: 'volume', tone: 'accent', compact: true })}
                          </Box>
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant={hasSingleSelectionTrip ? 'contained' : 'text'}
                        disabled={!tripSummary.pointCount}
                        onClick={() => handleMoveScopeSelect(buildMoveScopeFromTripSummary(tripSummary))}
                        sx={{ minWidth: 0, px: hasSingleSelectionTrip ? 1.1 : 0.6 }}
                      >
                        Move
                      </Button>
                    </Box>
                  </Box>
                ))}

                {totalSelectedPoints === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ px: 0.2 }}>
                    {editSourceTrip
                      ? `Select touch points on ${editSourceTrip} to start a move.`
                      : 'Draw a selection on the map to start a move.'}
                  </Typography>
                ) : null}

                {!hasSingleSelectionTrip ? (
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    disabled={!totalSelectedPoints}
                    onClick={() => handleMoveScopeSelect(buildMoveAllScope(activeSelectionSummary))}
                    sx={{ mt: 0.2 }}
                  >
                    Move All
                  </Button>
                ) : null}
              </Stack>
            )}
          </Box>
        </Paper>
      )}

      {geoCorrectionTouchPoint && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            right: 18,
            bottom: moveCardVisible ? 220 : 20,
            p: 0,
            zIndex: 3,
            minWidth: 320,
            maxWidth: 360,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)',
            boxShadow: mode === 'dark'
              ? '0 14px 32px rgba(0,0,0,0.56)'
              : '0 14px 32px rgba(15,23,42,0.18)',
          }}
        >
          <Box
            sx={{
              px: 1.6,
              py: 1.2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: mode === 'dark'
                ? 'linear-gradient(120deg, rgba(239,68,68,0.22), rgba(251,191,36,0.18))'
                : 'linear-gradient(120deg, rgba(239,68,68,0.12), rgba(251,191,36,0.12))',
              borderBottom: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.12)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
              Correct Location
            </Typography>
            <IconButton size="small" onClick={onCancelGeoCorrection}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 1.6 }}>
            <Stack spacing={1}>
              <Typography variant="body2">
                <Box component="span" sx={{ color: 'text.secondary', minWidth: 126, display: 'inline-block' }}>Touch Point:</Box>
                {geoCorrectionTouchPoint.name || geoCorrectionTouchPoint.tpId}
              </Typography>
              <Typography variant="body2">
                <Box component="span" sx={{ color: 'text.secondary', minWidth: 126, display: 'inline-block' }}>Current:</Box>
                {`${geoCorrectionTouchPoint.lngLat[1].toFixed(6)}, ${geoCorrectionTouchPoint.lngLat[0].toFixed(6)}`}
              </Typography>
              <Typography variant="body2">
                <Box component="span" sx={{ color: 'text.secondary', minWidth: 126, display: 'inline-block' }}>Draft:</Box>
                {geoCorrectionDraftLngLat
                  ? `${geoCorrectionDraftLngLat[1].toFixed(6)}, ${geoCorrectionDraftLngLat[0].toFixed(6)}`
                  : 'Click on the map to place the corrected pin'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pan and zoom as needed. Each new map click replaces the draft location.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" color="inherit" fullWidth onClick={onCancelGeoCorrection}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  disabled={!geoCorrectionDraftLngLat || geoCorrectionSubmitting}
                  onClick={onConfirmGeoCorrection}
                  startIcon={geoCorrectionSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  {geoCorrectionSubmitting ? 'Saving...' : 'Confirm'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      )}
      
      {/* Hovered info displays */}
      {allowTripHoverCards && activeTripInfo && activeTripInfoAnchorClient && (
        (() => {
          const isVerifyGroupTrip = (activeTripInfo.tripMeta?.status || '').toUpperCase() === 'VERIFY_GROUP';
          const baseTripCardModel = buildTripCardModel(activeTripInfo, {
            tenant: taskInfo?.tenant,
            isVerifyGroup: isVerifyGroupTrip,
          });
          const isJumboBuyTenant = String(taskInfo?.tenant ?? '').toLowerCase().includes('jumbobuy');
          const tripCardModel = isJumboBuyTenant
            ? {
                ...baseTripCardModel,
                primaryMetrics: baseTripCardModel.primaryMetrics.filter((metric) =>
                  ['weight', 'points', 'distance', 'duration', 'volume'].includes(metric.key)
                ),
                capacityTags: baseTripCardModel.capacityTags.filter((tag) => tag.key !== 'avl-crates'),
              }
            : baseTripCardModel;
          const accentColor = getTripAccentColor(activeTripInfo);
          return (
            <Portal>
              <Paper
                ref={updateTripInfoCardSize}
                elevation={3}
                onMouseEnter={() => {
                  tripCardHoveredRef.current = true;
                  clearTripCardHideTimer();
                }}
                onMouseLeave={() => {
                  tripCardHoveredRef.current = false;
                  if (!externalTripInfo) {
                    setHoveredTripInfo(null);
                    setInfoPosition(null);
                  }
                }}
                sx={{
                  position: 'fixed',
                  left: tripInfoPlacement.left,
                  top: tripInfoPlacement.top,
                  p: 0,
                  zIndex: 1600,
                  maxWidth: 430,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)',
                  boxShadow: mode === 'dark'
                    ? '0 10px 30px rgba(0,0,0,0.55)'
                    : '0 10px 30px rgba(15,23,42,0.18)',
                  pointerEvents: 'auto',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    px: 1.5,
                    py: 1.35,
                    pl: 1.8,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: accentColor,
                    },
                  }}
                >
                  <DenseEntityCardBody
                    model={tripCardModel}
                    density="compact"
                    metricLayout="row"
                    titleIcon="vehicle"
                    compactTitle
                    singleLinePrimaryMetrics
                    hideMetricDetails
                    contentOrder={['primaryMetrics', 'capacityTags', 'highlightTags', 'breachTags', 'footer']}
                    actions={
                      showDestinationChooser ? null : (
                        <React.Fragment>
                          {onOpenTripTimeline && canBuildTimelineStripData(activeTripInfo) ? (
                            <Tooltip title="Open timeline">
                              <IconButton
                                size="small"
                                onClick={() => onOpenTripTimeline(activeTripInfo.name)}
                                sx={{
                                  bgcolor: mode === 'dark' ? 'rgba(15,23,42,0.36)' : 'rgba(255,255,255,0.72)',
                                  border: '1px solid',
                                  borderColor: mode === 'dark' ? 'rgba(125,211,252,0.18)' : 'rgba(14,116,144,0.16)',
                                  color: mode === 'dark' ? '#67e8f9' : '#0f766e',
                                  '&:hover': {
                                    bgcolor: mode === 'dark' ? 'rgba(8,47,73,0.62)' : 'rgba(236,254,255,0.92)',
                                  },
                                }}
                              >
                                <ScheduleOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {editEnabled && onStartTripEdit ? (
                            <Tooltip title="Edit this trip">
                              <IconButton
                                size="small"
                                onClick={() => onStartTripEdit(activeTripInfo.name)}
                                sx={{
                                  bgcolor: mode === 'dark' ? 'rgba(15,23,42,0.36)' : 'rgba(255,255,255,0.72)',
                                  border: '1px solid',
                                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.14)',
                                  '&:hover': {
                                    bgcolor: mode === 'dark' ? 'rgba(15,23,42,0.56)' : 'rgba(255,255,255,0.92)',
                                  },
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </React.Fragment>
                      )
                    }
                  />
                </Box>
              </Paper>
            </Portal>
          );
        })()
      )}
      
      {!inspectMode && (
        <TouchPointPreviewOverlay
          touchPoint={externalTpInfo?.tp ?? hoveredTouchPoint ?? null}
          anchorClientPosition={externalTpInfo?.position ?? tpInfoPosition}
          tenant={taskInfo?.tenant}
          lookups={lookups}
          mode={mode}
          onMouseEnter={() => {
            tpCardHoveredRef.current = true;
            clearTouchPointCardHideTimer();
          }}
          onMouseLeave={() => {
            tpCardHoveredRef.current = false;
            scheduleTouchPointCardHide();
          }}
          onStartGeoCorrection={onStartGeoCorrection}
        />
      )}

      {/* External hover info from table/cards */}
      {!inspectMode && !hoveredTouchPoint && externalHover && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: editMode ? 120 : 20,
            transform: 'translateX(-50%)',
            p: 1.5,
            zIndex: 20,
            maxWidth: 320,
          }}
        >
          {(() => {
            const type = externalHover.type;
            const id = externalHover.id;
            let title = '';
            let address = '';
            let hours = '';
            if (type === 'origin') {
              title = 'Origin';
              const o = lookups?.origin;
              address = o?.address || '';
              if (o?.hourStart || o?.hourEnd) hours = `${o?.hourStart ?? ''} - ${o?.hourEnd ?? ''}`;
            } else if (type === 'client') {
              title = `Pickup ${id}`;
              const c = lookups?.clients?.[id];
              address = c?.address || '';
              if (c?.hourStart || c?.hourEnd) hours = `${c?.hourStart ?? ''} - ${c?.hourEnd ?? ''}`;
            } else if (type === 'drop') {
              title = `Drop ${id}`;
              const d = lookups?.drops?.[id];
              address = d?.address || '';
              if (d?.hourStart || d?.hourEnd) hours = `${d?.hourStart ?? ''} - ${d?.hourEnd ?? ''}`;
            }
            return (
              <>
                <Typography variant="subtitle2" gutterBottom>{title}</Typography>
                {!!address && (
                  <Typography variant="body2" gutterBottom>
                    {address}
                  </Typography>
                )}
                {!!hours && (<Typography variant="body2">Hours: {hours}</Typography>)}
              </>
            );
          })()}
        </Paper>
      )}
    </Box>
  );
};

export default MapView;
